// Boston's Acorn Street look (the registry's `looks`; boston.js, the Charles at blue hour, is the city's first), "Snow
// on Acorn Street's cobbles". A December night about nine o'clock, light snow falling and a thin layer of it settled on
// the tops of the cobbles, the sills, the steps and the lamps' hoods; wreaths and red bows on the doors and the gas lamps,
// an electric candle in the windows (the old Beacon Hill custom), warm rooms behind the glass. Cosy and quiet.
// Where we stand: on the cobbles at the top of Acorn Street on Beacon Hill, a few metres in from Willow Street (42.3577 N,
// 71.0685 W), at eye height (1.6 m), looking down the lane's whole length (about 70 m, the OpenStreetMap way) to the
// west-south-west (bearing 254) and downhill towards West Cedar Street: the view of the famous photographs. One-point
// perspective: level lines meet at (VX, HY); the lane falls 4.5 cm a metre, so its own lines meet 25 rows lower.
// Left to right, as seen from there: the south side's ten rowhouses, built in the 1820s for the coachmen and cooks of the
// Chestnut and Mount Vernon Street mansions (red brick, three storeys and attics, black shutters, granite lintels and
// steps, iron railings and boot scrapers, painted doors with brass knockers under lit transoms and fanlights, window
// boxes, a lantern by each door, ivy on a few fronts, the Stars and Stripes on its bracket half-way down, a cast-iron
// balcony, Christmas trees in some windows), their brick sidewalk and granite curb and the lamp posts along it; the river-stone cobbles down the middle; the narrow brick
// sidewalk on the north side and its lamp posts (the nearest wound with a garland and a red bow); the high brick walls
// of the Mount Vernon Street houses' hidden gardens, bare trees over them and the backs of those houses beyond, lit;
// the tall flank of the corner building at West Cedar Street, where a café on Charles Street has its sign painted (the
// city's billboard). Across West Cedar a brick house with an arched fanlight closes the view, as it does in the photos.
// What the brief hoped for but this spot can't show: the State House's gold dome stands behind the viewer (500 m east),
// the Charles is behind West Cedar and Charles Street (450 m west) and no skyline shows over the roofs. The Church of the
// Advent's spire (52 m, 244 m away) stands 18 degrees right of the lane, behind the Mount Vernon Street houses: its bells
// are heard (a big moment), and its pigeons answer them, but the spire isn't drawn. Cedar Lane Way doesn't meet Acorn
// Street; West Cedar's houses close the view. The real lane also has a sidewalk on its north side, so both are drawn.
// render(t, { crowd }) thins the people (see CROWD); crowd 0 draws nobody (the lamps, the snow and the cat stay).
//
// The loop's timeline (seconds):
//   2-28      two friends come round the corner from West Cedar and up the lane; at 14-20 one slips on the cobbles,
//             windmills, is caught by the other and they laugh; they let themselves in at a door (24-28)
//   28-36     a grey squirrel runs along the garden wall (again 186-194)
//   28-44     two neighbours carry their Christmas tree up from West Cedar and in at their door
//   36-42     a car crawls along West Cedar across the end of the lane, right to left (164-170 left to right,
//             226-232 right to left), its headlights sweeping the end house
//   50-84     BIG: the bells of the Church of the Advent ring a peal down the hill; the pigeons on the corner building's
//             parapet start up at the first strike, wheel over the lane and settle again; the shoveller stops to listen
//             and someone comes to a window
//   56-100    a couple come out arm in arm with their dog in a red coat (the door's light spills on the snow), say goodnight
//             to the shoveller, walk down the lane (the dog stops to sniff the snow at 68-72) and turn right on West Cedar
//   64-90     a woman stands in her open doorway with a mug, watching the snow
//   96-141    a delivery cyclist walks his bike up the cobbles, hands a bag in at a door (117-123) and walks it back down
//   112-118   someone under an umbrella hurries along West Cedar (150-156 a dog walker the other way)
//   122-164   BIG: a heavier flurry sweeps in from the right, slants the snow, veils the end of the lane and eases off; a
//             gust tugs the wreath off the lamp post by the steps (138); the photographer shields the camera
//   166-190   a father pulls his little girl on a sled up from West Cedar and they go in
//   166-194   the neighbour comes out, picks the wreath out of the snow, shakes it off and hangs it on the post again,
//             steps back to look and goes in
//   194-236   BIG: carollers come along West Cedar behind their lantern, stop at the foot of the lane and sing up it
//             (202-226; the photographer shoots them, the shoveller leans on his shovel, a neighbour listens in his open
//             doorway at 206-222), then walk on
//   all loop  snow falls at three depths and glitters in the lamps' light, low cloud drifts over the roofs lit by the
//             city, the gas lamps burn steady, chimneys smoke, the wind lifts the dry powder off the cobbles in low wisps
//             and stirs the ivy on the walls (its leaves flip pale), the bare trees over the garden walls and the window
//             boxes' evergreens; clumps of snow let go of the sills and the coping; windows switch on and off, their
//             candles glow, Christmas trees twinkle behind the glass and now and then someone crosses a lit room; the cat
//             in the window flicks its tail, the flag stirs, breath clouds rise, the resident shovels his steps and the
//             photographer at the tripod checks the camera's screen now and then
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, hash, rng, bayer, hex,
  makeCanvas, makeHalo, drawHalo, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather, stormLight } from '../engine/weather.js';

let ctx;                                            // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= The view: one-point perspective down the lane ================= */
// World metres: X across the lane (right +, towards the gardens), Z down it (away from the viewer), Y up, 0 at the
// cobbles under the viewer's feet. The lane falls GR a metre, so its surface at Z is Y = -GR·Z.
const F = 560;                                      // focal length in px: a 46-degree view, like the photographs
const VX = 268, HY = 140;                           // where level lines meet (eye level)
const EYE = 1.6, GR = 0.045, CAMX = 0.4;            // eye height, the lane's fall, where across it we stand
const XL = -2.5, XCL = -1.25, XCR = 1.45, XR = 2.2; // the house fronts, the two curbs, the garden walls
const ZEND = 68, ZH = 80;                           // the lane meets West Cedar Street; the house across it
const WCY = -GR * ZEND;                             // West Cedar runs level across the foot of the lane
const ACCENT = '#44bb11';                           // the city's: the Green Line's green
const gY = Z => (Z > ZEND ? WCY : -GR * Z);         // the ground's height at Z
const sx = (X, Z) => VX + F * (X - CAMX) / Z;       // screen column of a point
const sy = (Y, Z) => HY + F * (EYE - Y) / Z;        // screen row of a point at height Y (absolute)
const syG = (h, Z) => sy(gY(Z) + h, Z);             // screen row of a point h metres over the ground at Z
const zOfL = x => F * (CAMX - XL) / (VX - x);       // the depth of a column on the house fronts (x < VX)
const zOfR = x => F * (XR - CAMX) / (x - VX);       // the depth of a column on the garden walls (x > VX)
const zOfG = y => F * EYE / (y - HY - F * GR);      // the depth of a row on the lane's surface (y > HY + F·GR)
const pxm = Z => F / Z;                             // px a metre at depth Z

/* ================= The houses along the south side (left) ================= */
// [z0, z1, door at, storeys, cornice m, brick, door colour, attic]: about 6.3 m fronts of three bays, the door in one
// end bay; floors level within a house, so the street's fall shows as a step at every party wall.
const BRICKS = [
  ['#2a1414', '#3e1d19', '#55271f', '#6c3426', '#84432f'],
  ['#2a1616', '#3c201c', '#522b23', '#683a2c', '#7e4935'],
  ['#281418', '#3a1c1e', '#4e2626', '#653230', '#7c4038'],
  ['#2c1814', '#40241c', '#563024', '#6e3e2c', '#864e36'],
];
const DOORS = { black: ['#0c0c10', '#1a1a20', '#2a2a32'], green: ['#0a1610', '#132a1c', '#1e3e2a'], red: ['#2a0c0c', '#4a1414', '#6a2020'],
  white: ['#8a8c94', '#b8bac2', '#dcdee4'], blue: ['#0c1222', '#16203a', '#223052'] };
const HOUSES = [
  { z0: 3.0, z1: 9.6, door: 8.2, st: 3, corn: 10.0, brick: 0, col: 'black', attic: 'dormer' },
  { z0: 9.6, z1: 16.0, door: 14.6, st: 3, corn: 9.7, brick: 1, col: 'white', attic: 'flat' },
  { z0: 16.0, z1: 22.2, door: 16.5, st: 4, corn: 11.6, brick: 2, col: 'green', attic: 'mansard' },
  { z0: 22.2, z1: 28.4, door: 26.9, st: 3, corn: 10.1, brick: 3, col: 'red', attic: 'dormer' },   // the neighbour's
  { z0: 28.4, z1: 34.6, door: 33.2, st: 3, corn: 9.8, brick: 0, col: 'black', attic: 'flat' },    // the couple's
  { z0: 34.6, z1: 40.8, door: 35.0, st: 3, corn: 10.4, brick: 1, col: 'blue', attic: 'dormer', balcony: true },   // the shoveller's
  { z0: 40.8, z1: 47.0, door: 45.6, st: 3, corn: 10.0, brick: 2, col: 'white', attic: 'flat', flag: true },     // the delivery
  { z0: 47.0, z1: 53.2, door: 47.4, st: 4, corn: 11.2, brick: 3, col: 'green', attic: 'mansard' },             // the friends'
  { z0: 53.2, z1: 60.0, door: 58.6, st: 3, corn: 9.9, brick: 0, col: 'red', attic: 'dormer' },
  { z0: 60.0, z1: 68.0, door: 60.4, st: 3, corn: 10.3, brick: 1, col: 'black', attic: 'flat' },
];
const DOOR_W = 1.1;
// the ground floor sits up a few granite steps; window rows (sill, head) above each house's own base
const FLOORS = [[1.3, 2.9], [4.0, 5.7], [6.8, 8.3], [9.4, 10.5]];
const houseAt = Z => { for (const h of HOUSES) if (Z < h.z1) return h; return HOUSES[HOUSES.length - 1]; };
const baseOf = h => -GR * h.z0;                     // a house's floor datum: its uphill end's sidewalk
const WINDOWS = h => {                              // the bays' centres along Z, the door's bay first
  const doorNear = h.door - h.z0 < (h.z1 - h.z0) / 2, w = h.z1 - h.z0;
  const dc = h.door + DOOR_W / 2;
  return doorNear ? { dc, ground: [h.z0 + w * 0.5, h.z0 + w * 0.8], upper: [dc, h.z0 + w * 0.5, h.z0 + w * 0.8] }
    : { dc, ground: [h.z0 + w * 0.2, h.z0 + w * 0.5], upper: [h.z0 + w * 0.2, h.z0 + w * 0.5, dc] };
};

/* ================= Lamps ================= */
// Beacon Hill's gas lamps: copper lanterns on black cast-iron posts at the curbs, and lanterns on brackets by the doors.
// They burn day and night with a steady mantle; the light breathes only very slightly.
const POSTS = [
  { X: XCL - 0.22, Z: 13 }, { X: XCL - 0.22, Z: 26 }, { X: XCL - 0.22, Z: 44 },
  { X: XCR + 0.2, Z: 8, garland: true }, { X: XCR + 0.2, Z: 18 },
];
const WREATH_POST = POSTS[1];                       // the post the gust robs of its wreath (by the neighbour's steps)
const WREATH_H = 1.3;                               // its centre over the ground: below the face of the shoveller behind it
const LANTERNS = HOUSES.map(h => {                  // by each door, on its far side (its near side if the house ends there)
  const far = h.door + DOOR_W + 0.32;
  return { X: XL + 0.28, Z: far < h.z1 - 0.15 ? far : h.door - 0.32, Y: baseOf(h) + 2.45 };
});
const POST_Y = 3.05;                                // a post lantern's centre over the ground
// every light in world metres (lantern centre), for the facades, the snow on the ground and the flakes
const LIGHTS = [...POSTS.map(p => ({ X: p.X, Z: p.Z, Y: gY(p.Z) + POST_Y, I: 1 })), ...LANTERNS.map(l => ({ X: l.X, Z: l.Z, Y: l.Y, I: 0.75 }))];
const lampAt = (X, Y, Z) => {                       // the gas light reaching a point (0..~1.5), world metres
  let s = 0;
  for (const L of LIGHTS) {
    const dz = Z - L.Z;
    if (dz > 14 || dz < -14) continue;
    const d2 = (X - L.X) ** 2 + (Y - L.Y) ** 2 * 0.7 + dz * dz;
    s += L.I * 2.2 / (1 + d2 * 0.55);
  }
  return s;
};

/* ================= The far end, the right side and the billboard ================= */
const CORNER = { X0: XR, X1: 8.5, Z: 62, top: 14.5 };  // the corner building's east flank, facing up the lane
const BB = { x: 286, y: 57, w: 50, h: 20 };          // the painted sign on it (the city's billboard)
const GARDEN = { top: 3.1 };                         // the garden walls' coping, over the sidewalk
const NEAR_R = { z1: 6.8 };                          // the house at the Willow Street corner, on the right
const GDOOR = { z0: 14.0, z1: 15.1 };                // a garden door in the wall

/* ================= Timeline ================= */
const FRIENDS = { t0: 2, up: 6, slip: 14, laugh: 17, door: 24, t1: 28, zDoor: 48.0 };
const CARS = [{ t0: 36, dir: -1, col: '#5a2a2a' }, { t0: 164, dir: 1, col: '#2a3a52' }, { t0: 226, dir: -1, col: '#3a3a40' }];
const BELLS = { t0: 50, t1: 84 };
const BELL_T = [50, 52.4, 54.8, 57.2, 59.6, 62, 64.4, 66.8, 69.2, 71.6, 74, 76.4, 78.8, 81.4];
const COUPLE = { door: 56, out: 58, sniff: [68, 72], end: 92, t1: 100 };
const DELIV = { t0: 96, up: 100, door: 117, back: 123, end: 137, t1: 141 };
const TREE = { t0: 28, door: 40.5, t1: 44 };
const SLED = { t0: 166, door: 186, t1: 190 };
const DOORSTEP = { t0: 64, t1: 90 };
const LISTEN = { t0: 206, t1: 222 };
const WC_WALK = [{ t0: 112, t1: 118, dir: -1, umbrella: true }, { t0: 150, t1: 156, dir: 1, dog: true }];
const FLURRY = { t0: 122, p0: 132, p1: 152, t1: 164, gust: 138 };
const NEIGH = { door: 166, t1: 194 };
const CAROLS = { t0: 194, stop: 202, go: 226, t1: 236 };
const SQUIRREL = [28, 186];
// The people: about half of a cast of 22 by default. render(t, { crowd }) shows those whose rank is under crowd; rank 0
// (the shoveller, the photographer, the neighbour, the delivery cyclist and the carollers) stays while anybody does;
// crowd 0 draws nobody and takes the shovel, the tripod, the bike, the wreath's fall and rehanging, the dog and the
// carollers' lantern with them. The cars, the cat, the pigeons and the squirrel stay.
const CROWD = 0.5;
const here = (rank, crowd) => crowd > 0 && (rank === 0 || rank < crowd);
const RANK = { shovel: 0, photo: 0, neigh: 0, deliv: 0, carols: 0, friends: 0.14, couple: 0.3, windows: 0.36, doorstep: 0.42,
  tree: 0.56, sled: 0.66, wc1: 0.82, wc2: 0.9 };

/* ================= Pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const MIXED = {};
function mixHex(a, b, t) {
  t = clamp(t, 0, 1);
  const k = a + b + Math.round(t * 100);
  if (MIXED[k]) return MIXED[k];
  const A = C(a), B = C(b);
  return (MIXED[k] = toHex(...A.map((v, i) => Math.round(lerp(v, B[i], t)))));
}
/** ImageData-backed painter for prerendering */
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    line(x0, y0, x1, y1, col) {
      const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
      for (let k = 0; k <= n; k++) this.set(lerp(x0, x1, k / n), lerp(y0, y1, k / n), col);
    },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 (soft < 1 narrows the dithered band) */
function pick(ramp, v, i, j, soft = 1) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * soft) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only); optional period px in x for tileable layers */
function vnoise(x, y, s, px = 0) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const X0 = px ? ((xi % px) + px) % px : xi, X1 = px ? (((xi + 1) % px) + px) % px : xi + 1;
  const a = hash(X0, yi, s), b = hash(X1, yi, s), c = hash(X0, yi + 1, s), d = hash(X1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = pix(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
/** crop a painter to its opaque bounding box (build time) */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const blit = (o, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(o.c, o.x, o.y); ctx.globalAlpha = 1; };
const dot = (x, y, col) => { ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), 1, 1); };
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(Math.round(x), Math.round(y), w, h); };
const inWin = (t, a, b) => t >= a && t < b;
/** the flurry's strength, 0..1 */
const flurry = t => smooth(FLURRY.t0, FLURRY.p0, t) * (1 - smooth(FLURRY.p1, FLURRY.t1, t));

/* ================= The sky: a low snow sky, lit from under by the city ================= */
// Snow cloud over Boston glows: it holds the city's light, warmest low down over the end of the lane (the Back Bay and
// Cambridge lie that way). No moon, no stars; the cloud drifts slowly over the roofs.
const SKY = ['#121426', '#16182d', '#1b1c33', '#211f39', '#28233f', '#302744', '#382b48', '#41304c', '#4a354f', '#533a52',
  '#5c4054', '#664656'];
const skyV = (x, y) => Math.pow(clamp(y / 176, 0, 1), 1.25) * 0.8 + 0.16 * Math.exp(-(((x - 262) / 120) ** 2)) * smooth(20, 150, y);
function renderSky() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) p.set(x, y, pick(SKY, skyV(x, y) + (vnoise(x / 60, y / 14, 3) - 0.5) * 0.06, x, y));
  return p.done();
}
const hazeAt = (x, y) => SKY[Math.min(SKY.length - 1, Math.round(clamp(skyV(x, y), 0, 1) * (SKY.length - 1)))];
/* the low cloud deck, tileable (period W): mottled, dark gaps and banks lit from under, all across the sky */
function makeDeck(seed, h) {
  const p = pix(W, h), per = Math.round(W / 46);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const n = vnoise(x / 46, y / 13, seed, per) * 0.62 + vnoise(x / 12, y / 5, seed + 3, W / 12) * 0.38 + (bayer(x, y) - 0.5) * 0.22;
    const fade = smooth(0, 14, y) * (1 - smooth(h - 22, h, y));
    if (n > 0.62 && fade > bayer(x + 1, y + 2)) p.set(x, y, n > 0.74 ? '#7a6282' : '#5a4a68', 120);
    else if (n < 0.38 && fade > bayer(x + 2, y + 1)) p.set(x, y, '#0c0c1c', 110);
  }
  return p.done();
}
/* low cloud, tileable (period W): soft banks, their bellies warmed by the city */
function makeCloud(seed, h, col, colLo, a, scale) {
  const p = pix(W, h), per = Math.round(W / scale);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const n = vnoise(x / scale, y / (scale * 0.32), seed, per) * 0.65 + vnoise(x / (scale * 0.4), y / (scale * 0.14), seed + 5, Math.round(W / (scale * 0.4))) * 0.35;
    const v = n * smooth(0, h * 0.3, y) * (1 - smooth(h * 0.65, h, y));
    if (v > 0.34 + bayer(x, y) * 0.22) p.set(x, y, v > 0.5 && y > h * 0.45 ? colLo : col, a);
  }
  return p.done();
}

/* ================= Across West Cedar Street: the house that closes the view ================= */
// A three-storey brick house with an attic, seen square on at ZH: black shutters, granite lintels, a white door under an
// arched fanlight up three granite steps, a wreath on it, a candle in every window, the lamp post in front of it.
const EH = { x0: 236, x1: 302, X0: -4.0, X1: 5.2 };
const CHIM9 = (() => { const Ho = HOUSES[9], Z = 67.4, Y = baseOf(Ho) + Ho.corn + 1.8; return { x: Math.round(sx(XL - 0.32, Z)), y: Math.round(sy(Y, Z)), Z, Y }; })();
const ehx = X => sx(X, ZH), ehy = h => syG(h, ZH);
function renderEndHouse(r) {
  const p = pix(W, H), s = pxm(ZH);
  const top = Math.round(ehy(9.9)), base = Math.round(ehy(0));
  for (let y = top; y <= base; y++) for (let x = EH.x0; x < EH.x1; x++) {
    const X = CAMX + (x - VX) / s, h = (base - y) / s;
    let v = 0.42 + (hash(x >> 1, y, 41) - 0.5) * 0.12 + ((y & 1) ? -0.04 : 0) + 0.35 * Math.exp(-(((X - 1.2) / 2.6) ** 2) - (((h - 2.8) / 2.4) ** 2));
    let col = pick(BRICKS[2], v * 0.8, x, y, 0.6);
    if (h < 0.55) col = pick(['#3a3a44', '#565662', '#74747e'], 0.5 + (hash(x, y, 2) - 0.5) * 0.3, x, y);
    p.set(x, y, mixHex(col, '#2a2440', 0.25));
  }
  for (let x = EH.x0 - 1; x <= EH.x1; x++) {                                     // the cornice, snow along it
    const y0 = Math.round(ehy(9.9));
    p.set(x, y0, '#e8e6f0'); p.set(x, y0 + 1, '#9a96a8'); p.set(x, y0 + 2, '#5a5468'); p.set(x, y0 + 3, '#2a2234');
  }
  for (let y = Math.round(ehy(12.2)); y < top; y++) {                              // the slate roof, snow on it, two dormers
    const k = (y - ehy(12.2)) / (top - ehy(12.2));
    for (let x = EH.x0 + 2; x < EH.x1 - 2; x++) p.set(x, y, pick(['#3a3a4e', '#6a6a82', '#a8aac0', '#d8dae8'], 0.55 + 0.35 * (1 - k) + (hash(x, y, 9) - 0.5) * 0.4, x, y));
  }
  for (const X of [-1.9, 2.5]) {
    const x0 = Math.round(ehx(X) - 3), y0 = Math.round(ehy(11.6));
    for (let y = y0; y < top; y++) for (let x = x0; x < x0 + 7; x++) p.set(x, y, y === y0 ? '#e8e8f2' : '#3a3444');
    p.fill(x0 + 2, y0 + 2, 3, top - y0 - 2, '#e8b060'); p.set(x0 + 3, y0 + 4, '#fff0c0');
  }
  for (const X of [-1.1, 2.1]) {                                                  // chimneys on the ridge
    const x0 = Math.round(ehx(X)), y0 = Math.round(ehy(13.4)), y1 = Math.round(ehy(11.8));
    for (let y = y0; y <= y1; y++) for (let x = x0 - 2; x <= x0 + 2; x++) p.set(x, y, y === y0 ? '#dcdce8' : y === y0 + 1 ? '#6a3a2e' : pick(BRICKS[0], 0.5, x, y));
  }
  const lit = (x0, y0, w, h, col, k) => {                                          // a window: frame, muntins, a candle
    for (let y = y0; y < y0 + h; y++) for (let x = x0; x < x0 + w; x++) {
      let c = pick(col, 0.35 + 0.5 * (y - y0) / h, x, y);
      if (x === x0 + (w >> 1) || y === y0 + (h >> 1)) c = '#5a3a24';
      p.set(x, y, c);
    }
    p.set(x0 + (w >> 1), y0 + h - 3, '#f4f0e8'); p.set(x0 + (w >> 1), y0 + h - 4, '#fff4b0');   // the candle
  };
  const WARMG = ['#7a4a26', '#b8783a', '#e8ac5c', '#ffd890'], DIMG = ['#2a2230', '#3a2e3a', '#5a4448'];
  FLOORS.slice(0, 3).forEach(([s0, s1], f) => {
    for (const X of f ? [-1.9, 0.3, 2.5] : [-1.9, 2.5]) {
      const w = 7, x0 = Math.round(ehx(X) - 3), y0 = Math.round(ehy(s1)), hh = Math.round(ehy(s0)) - y0;
      p.fill(x0 - 3, y0, 2, hh, '#101014'); p.fill(x0 + w + 1, y0, 2, hh, '#101014');   // shutters
      lit(x0, y0, w, hh, (f + X) % 3 === 1 ? DIMG : WARMG, f);
      for (let x = x0 - 1; x <= x0 + w; x++) { p.set(x, y0 - 1, '#c8c4cc'); p.set(x, y0 + hh, '#e8e6f0'); }   // lintel, sill under snow
    }
  });
  const dx = Math.round(ehx(0.3) - 4), dTop = Math.round(ehy(2.75)), dBot = Math.round(ehy(0.6));   // the door and its fanlight
  for (let y = dTop; y < dBot; y++) for (let x = dx; x < dx + 8; x++) p.set(x, y, x === dx || x === dx + 7 ? '#a8aab4' : (y - dTop) % 5 === 4 ? '#b8bac4' : '#dcdee6');
  for (let k = 0; k < 4; k++) for (let x = dx + Math.abs(2 - k) - 1; x <= dx + 8 - Math.abs(2 - k); x++) p.set(x, dTop - 4 + k, k ? '#ffd890' : '#e8e6f0');
  for (const [x, y] of [[dx + 2, dTop - 3], [dx + 4, dTop - 3], [dx + 6, dTop - 3]]) p.set(x, y, '#c8a060');
  for (let k = 0; k < 3; k++) for (let x = dx - 2 - k; x < dx + 10 + k; x++) p.set(x, dBot + k, k === 0 ? '#e8e8f0' : k === 1 ? '#8a8a96' : '#d8d8e4');   // steps
  for (let y = dTop + 3; y < dTop + 9; y++) for (let x = dx + 2; x < dx + 6; x++) if (Math.hypot(x - dx - 3.5, y - dTop - 5.5) > 1 && Math.hypot(x - dx - 3.5, y - dTop - 5.5) < 2.8) p.set(x, y, '#1e4a26');
  p.set(dx + 3, dTop + 8, '#c8282a'); p.set(dx + 4, dTop + 8, '#c8282a');          // the wreath's bow
  for (const X of [-3.9, 5.1]) for (let y = top; y <= base; y++) p.set(Math.round(ehx(X)), y, '#140c10');   // its neighbours' party walls
  {                                                                                // the last rowhouse's gable chimney, over the corner
    const Z = CHIM9.Z, x0 = Math.round(sx(XL - 0.65, Z)), x1 = Math.round(sx(XL - 0.02, Z)), y0 = CHIM9.y, y1 = Math.round(sy(CHIM9.Y - 2.2, Z));
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) p.set(x, y, y === y0 ? '#e0dcea' : y === y0 + 1 ? '#7a4a3a' : pick(BRICKS[1], 0.35 + (x === x1 ? 0.15 : 0), x, y));
  }
  return trim(p);
}
/* West Cedar Street: its far sidewalk and its snowy road, tyre tracks along it */
function renderWestCedar() {
  const p = pix(W, H), y0 = Math.round(syG(0, ZH)), y1 = Math.round(syG(0, ZEND + 0.2));
  for (let y = y0; y <= y1; y++) for (let x = 238; x < 300; x++) {
    const Z = F * (EYE - WCY) / (y - HY), X = CAMX + (x - VX) * Z / F;
    const sidewalk = Z > ZH - 2.2 || Z < ZEND + 1.4, track = Math.abs(Z - 73.6) < 0.35 || Math.abs(Z - 75.4) < 0.35;
    const lit = clamp(lampAt(X, WCY, Z) * 0.7 + 0.35 * Math.exp(-((X - 0.3) ** 2) / 9), 0, 1);
    p.set(x, y, pick(['#4a4a66', '#6e6c88', '#a49cb0', '#d8ccc4', '#f0e2c8'], (track && !sidewalk ? 0.25 : 0.45) + lit * 0.5, x, y));
  }
  return trim(p);
}

/* ================= The corner building's flank, and the café's painted sign on it ================= */
const cfx = X => sx(X, CORNER.Z), cfy = h => syG(h, CORNER.Z);
function renderCorner(withSign) {
  const p = pix(W, H), x0 = Math.round(cfx(CORNER.X0)), x1 = Math.round(cfx(CORNER.X1)), top = Math.round(cfy(CORNER.top)), base = Math.round(cfy(2.4));
  for (let y = top; y <= base; y++) for (let x = x0; x <= x1; x++) {
    let v = 0.4 + (hash(x >> 1, y, 77) - 0.5) * 0.14 + ((y % 3) === 0 ? -0.05 : 0) - 0.12 * (x - x0) / (x1 - x0) + 0.1 * smooth(base - 20, base, y);
    if (x === x0) v += 0.16;
    p.set(x, y, mixHex(pick(BRICKS[1], v * 0.85, x, y, 0.6), hazeAt(x, y), 0.18));
  }
  for (let x = x0 - 1; x <= x1 + 1; x++) { p.set(x, top - 2, '#e4e2ee'); p.set(x, top - 1, '#a8a4b8'); p.set(x, top, '#4a4458'); }   // the parapet's coping, snow on it
  for (const [wx, wy] of [[x0 + 8, cfy(7.6)], [x0 + 30, cfy(7.6)], [x0 + 8, cfy(4.6)]]) {                   // three small windows below the sign
    const X0 = Math.round(wx), Y0 = Math.round(wy);
    for (let y = Y0; y < Y0 + 9; y++) for (let x = X0; x < X0 + 6; x++) p.set(x, y, x === X0 + 3 || y === Y0 + 4 ? '#4a3020' : pick(['#8a5a2a', '#d0904a', '#f8c878'], 0.3 + 0.6 * (y - Y0) / 9, x, y));
    for (let x = X0 - 1; x <= X0 + 6; x++) { p.set(x, Y0 - 1, '#b8b4c0'); p.set(x, Y0 + 9, '#dcdae6'); }
  }
  for (let y = top + 2; y <= base; y++) p.set(x1 - 3, y, (y & 3) === 0 ? '#2a2a34' : '#1a1a22');                // a downpipe
  const s9 = pxm(CORNER.Z);                                                                                  // ivy climbing the flank from the gardens
  IVY_REC = true;
  for (let y = BB.y + BB.h + 6; y <= base; y++) for (let x = x0; x <= x1 - 5; x++) {
    const u = (x - x0) / s9, v = (base - y) / s9 + 2.4, m = vnoise(u / 1.6, v / 1.4, 44) * 0.8 + 0.3 - 0.3 * smooth(4, 10, v) * smooth(1.5, 5.5, u) + 0.1 * smooth(3, 2.4, v);
    const c = ivyAt(u, v, m, 0.2, x, y, bayer(x, y));
    if (c) p.set(x, y, c);
  }
  IVY_REC = false;
  if (withSign) {                                                                                            // the sign's frame and its two gooseneck lamps
    for (let x = BB.x - 1; x <= BB.x + BB.w; x++) { p.set(x, BB.y - 1, '#c8c4d0'); p.set(x, BB.y + BB.h, '#3a2a2a'); }
    for (const lx of [BB.x + 10, BB.x + BB.w - 11]) {
      // a stem up the brick, its arm out to a hood that faces down at the sign (the bulb lit under it per frame), snow on the hood
      p.line(lx, BB.y - 2, lx, BB.y - 5, '#1a1a22'); p.line(lx, BB.y - 5, lx + 2, BB.y - 6, '#1a1a22');
      for (let k = 2; k <= 4; k++) { p.set(lx + k, BB.y - 6, '#2a2a32'); p.set(lx + k, BB.y - 7, '#e8e6f0'); }
    }
  }
  return trim(p);
}
// the café's sign, painted on the brick: cream letters on oxblood, a gold rule, worn by the weather
function renderAd() {
  const { w, h } = BB, p = pix(w, h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) p.set(i, j, pick(['#3a1214', '#4e1a1a', '#622420'], 0.55 + (hash(i >> 1, j, 3) - 0.5) * 0.4 - j * 0.012, i, j));
  for (let i = 0; i < w; i++) { p.set(i, 0, '#22080a'); p.set(i, h - 1, '#22080a'); }
  for (let j = 0; j < h; j++) { p.set(0, j, '#22080a'); p.set(w - 1, j, '#22080a'); }
  const ink = (s, x, y, col) => { let xx = x; for (const ch of s) { const g = FONTS[ch]; if (g) g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') p.set(xx + i, y + j, col); }); xx += (g?.[0].length ?? 3) + 1; } };
  const wide = s => [...s].reduce((n, ch) => n + (FONTS[ch]?.[0].length ?? 3) + 1, 0) - 1, mid = s => Math.floor((w - wide(s)) / 2);
  ink('LAMPLIGHTER', mid('LAMPLIGHTER'), 2, '#f0d48a');
  for (const [s, y] of [['HOT COCOA', 8], ['CREAM PIE', 14]]) {                                     // the two lines, gold dashes either side
    const x0 = mid(s), x1 = x0 + wide(s) - 1;
    ink(s, x0, y, '#f4ece0');
    for (let k = 2; k <= 4; k++) { p.set(x0 - k, y + 2, '#c8a050'); p.set(x1 + k, y + 2, '#c8a050'); }   // a pixel clear of the words
  }
  return gradeAd(p.done(), { level: 0.8, tint: '#1a1430', mix: 0.12, wear: 0.42, seed: 17 });
}
const FONTS = {
  L: ['100', '100', '100', '100', '111'], A: ['010', '101', '111', '101', '101'], M: ['10001', '11011', '10101', '10001', '10001'],   // wide: a 3-wide M reads as H
  P: ['110', '101', '110', '100', '100'], I: ['111', '010', '010', '010', '111'], G: ['011', '100', '101', '101', '011'],
  H: ['101', '101', '111', '101', '101'], T: ['111', '010', '010', '010', '010'], E: ['111', '100', '110', '100', '111'],
  R: ['110', '101', '110', '101', '101'], O: ['010', '101', '101', '101', '010'], C: ['011', '100', '100', '100', '011'],
  ' ': ['000', '000', '000', '000', '000'],
};

/* ================= Over the garden walls: the backs of Mount Vernon Street, and bare trees ================= */
// The houses' rear walls stand about 18 m back across their gardens, four and five storeys, lit; one has its tree up.
const MV_X = 18.4;
function renderBacks(r) {
  const p = pix(W, H);
  const edges = [50, 57, 63.5, 70, 77, 84, 92, 100, 108, 116, 125];
  for (let x = 330; x < W; x++) {
    const Z = F * (MV_X - CAMX) / (x - VX);
    if (Z < 48 || Z > 125) continue;
    let k = 0; while (k < edges.length - 1 && Z > edges[k + 1]) k++;
    const st = [5, 4, 5, 4, 4, 5, 4, 5, 4, 4][k] ?? 4, hTop = st * 3.1 + 1.2, yTop = Math.round(sy(-GR * Z + hTop, Z)), yBot = Math.round(sy(-GR * Z + 2.6, Z));
    const party = Math.abs(Z - edges[k]) < Z * Z / 6000 + 0.2;
    for (let y = Math.max(0, yTop); y <= yBot; y++) {
      const h = -GR * Z + (EYE - (y - HY) * Z / F) + GR * Z;
      let v = 0.32 + (hash(x, y >> 1, 300 + k) - 0.5) * 0.12 + (k % 2 ? 0.05 : -0.03);
      let col = pick(k % 3 === 2 ? ['#1e1a26', '#2a2430', '#3a323e', '#4a404a'] : BRICKS[k % 4].slice(0, 4), v, x, y, 0.6);
      if (y === yTop) col = '#c8c6d4';
      else if (party) col = '#140e14';
      else {
        const fl = (h - 1) % 3.1, bay = frac((Z - edges[k]) / 2.1);
        if (fl > 1.1 && fl < 2.5 && bay > 0.3 && bay < 0.7 && h < hTop - 1) {
          const lit = hash(k, Math.floor((h - 1) / 3.1), Math.floor((Z - edges[k]) / 2.1)) < 0.5;
          col = lit ? pick(['#8a5a2e', '#c8884a', '#f0b868'], 0.3 + 0.5 * (fl - 1.1) / 1.4, x, y) : '#1a1620';
          if (lit && k === 3 && Math.floor((h - 1) / 3.1) === 1) col = ['#e83a3a', '#3ae86a', '#ffd040', '#4a8aff'][(x + y) % 4];   // a Christmas tree in the window
        }
      }
      p.set(x, y, mixHex(col, hazeAt(x, y), 0.3));
    }
    if (party && yTop > 2) for (let y = yTop - 4; y < yTop; y++) p.set(x, y, y === yTop - 4 ? '#c8c6d4' : '#2a1a1a');   // chimneys on the party walls
  }
  return trim(p);
}
// bare trees in the gardens: [X, Z, fork height, crown top, spread] (crowns kept off the sign)
const TREES = [{ X: 5.6, Z: 21, fork: 4.6, top: 13, spread: 4.2, seed: 5 }, { X: 8.6, Z: 35, fork: 5.0, top: 12, spread: 3.6, seed: 9 },
  { X: 11.5, Z: 50, fork: 5.2, top: 11.5, spread: 3.2, seed: 13 }];
const TWIGS = [];                                   // branch tips that stir in the wind (x, y, k, ph)
function renderTree(T, ti) {
  const pA = pix(W, H), pB = pix(W, H), r = rng(T.seed);
  let p = pA;
  const s = pxm(T.Z), bx = sx(T.X, T.Z), by = syG(GARDEN.top - 0.3, T.Z), fy = syG(T.fork, T.Z), ty = syG(T.top, T.Z);
  const keep = (x, y) => x >= 298 && !(x >= BB.x - 3 && x <= BB.x + BB.w + 2 && y >= BB.y - 8 && y <= BB.y + BB.h + 2);   // clear of the sign and the end of the lane
  const seg = (x0, y0, x1, y1, w, col, snow) => {
    const n = Math.max(1, Math.ceil(Math.hypot(x1 - x0, y1 - y0))), flat = Math.abs(y1 - y0) < Math.abs(x1 - x0) * 1.2;
    for (let k = 0; k <= n; k++) {
      const x = lerp(x0, x1, k / n), y = lerp(y0, y1, k / n);
      for (let d = 0; d < w; d++) if (keep(x + d, y)) {
        p.set(x + d, y, col);
        if (snow && flat && keep(x + d, y - 1) && !p.alpha(Math.round(x + d), Math.round(y - 1)) && hash(Math.round(x + d), Math.round(y), 5) < snow) p.set(x + d, y - 1, '#a8a8c4');
      }
    }
  };
  const tw = Math.max(1, Math.round(s * 0.38));
  seg(bx, by, bx, fy, tw, '#16121a', 0);
  for (let x = bx - tw; x < bx + 2 * tw; x++) for (let y = by - 1; y < by + 2; y++) p.set(x, y, '#16121a');
  const branch = (x, y, ang, len, w, depth) => {                                   // each limb bends a little at two knots
    let px0 = x, py0 = y, a = ang;
    p = depth >= 2 ? pB : pA;                                                     // the outer limbs sway in the wind
    for (let k = 0; k < 3; k++) {
      a += (r() - 0.5) * 0.35;
      const px1 = px0 + Math.cos(a) * len / 3, py1 = py0 + Math.sin(a) * len / 3;
      seg(px0, py0, px1, py1, w, depth > 3 ? '#201c26' : '#1a161e', depth > 1 && depth < 4 ? 0.3 : 0);
      px0 = px1; py0 = py1;
    }
    if (depth >= 5 || len < 3) { if (keep(px0, py0)) TWIGS.push({ x: Math.round(px0), y: Math.round(py0), k: 24 + Math.floor(r() * 30), ph: r(), ti }); return; }
    const n = depth < 2 ? 3 : 2;
    for (let i = 0; i < n; i++) branch(px0, py0, a + (i - (n - 1) / 2) * (0.5 + r() * 0.35) + (r() - 0.5) * 0.3, len * (0.62 + r() * 0.18), Math.max(1, w - 1), depth + 1);
  };
  const L = (fy - ty) * 0.42;
  for (let i = 0; i < 4; i++) branch(bx + (i % 2), fy, -Math.PI / 2 + (i - 1.5) * 0.45 + (r() - 0.5) * 0.2, L * (0.8 + r() * 0.3), Math.max(1, tw - 1), 0);
  return { a: trim(pA), b: trim(pB) };
}
/** how far a tree's outer limbs lean this moment: a pixel either way in the breeze, two in the flurry */
const treeSway = (ti, t) => Math.round(wave(t, 26 + ti * 7, ti * 0.31) * (0.7 + 1.5 * flurry(t)) + 0.35 * wave(t, 71 + ti * 5, ti * 0.6));

/* ================= Painting in perspective ================= */
// Every surface is painted by working back from each pixel to the metres it covers. Far off, a pixel spans a metre or
// more of a front, so it shows one point of that span, picked by its ordered-dither threshold: the colours stay crisp
// and each feature keeps its share of the pixels (a window a third of a pixel wide lights a third of them).
/** fill a convex polygon given in screen px; col(x, y) gives each pixel's colour (or null to skip) */
function fillPoly(p, pts, col) {
  let y0 = Infinity, y1 = -Infinity;
  for (const [, y] of pts) { y0 = Math.min(y0, y); y1 = Math.max(y1, y); }
  for (let y = Math.max(0, Math.ceil(y0 - 0.5)); y <= Math.min(H - 1, Math.floor(y1 - 0.5)); y++) {
    const yc = y + 0.5;
    let xa = Infinity, xb = -Infinity;
    for (let i = 0; i < pts.length; i++) {
      const [ax, ay] = pts[i], [bx, by] = pts[(i + 1) % pts.length];
      if ((ay <= yc && by > yc) || (by <= yc && ay > yc)) { const x = ax + (yc - ay) * (bx - ax) / (by - ay); xa = Math.min(xa, x); xb = Math.max(xb, x); }
    }
    if (xa > xb) continue;
    let a = Math.round(xa), b = Math.round(xb) - 1;
    if (b < a) b = a;                                                                  // never thinner than a pixel
    for (let x = Math.max(0, a); x <= Math.min(W - 1, b); x++) { const c = col(x, y); if (c) p.set(x, y, c); }
  }
}
/** where in its span of metres a column (or a row) takes its one sample: spread evenly, the same all down the column */
const colU = x => frac(x * 0.618034 + 0.31);
/** a world quad (four [X, Y, Z] corners) to screen points */
const proj = pts => pts.map(([X, Y, Z]) => [sx(X, Z), sy(Y, Z)]);

/* ================= The south side's fronts ================= */
const WIN_LIT = ['#6a3a1e', '#9a5a2c', '#c8823e', '#eaa85a', '#ffd088'], WIN_DARK = ['#0e0e18', '#161624', '#20223a', '#2c3050'];
const SHUT = ['#07090a', '#0e1312', '#161e1c', '#22302c'], GRANITE = ['#2c2c36', '#44444e', '#62626c', '#82828a', '#a4a4ac'];
const SNOWC = ['#3a3c58', '#52546e', '#6e6e88', '#8e8ca2', '#b0aabb', '#cfc6c8', '#ead8c4', '#f8e8cc'];
const WINDOW_OVERRIDE = new Map();                  // windows painted in their other state (the toggles' overlays)
const winKey = (hi, f, b) => hi * 100 + f * 10 + b;
const winLit = (hi, f, b) => WINDOW_OVERRIDE.has(winKey(hi, f, b)) ? WINDOW_OVERRIDE.get(winKey(hi, f, b)) : hash(hi, f, b + 7) < (f === 0 ? 0.7 : 0.48);
/* ivy: evergreen English ivy on a few fronts and the garden walls; its leaves flip pale in the wind (drawn per frame) */
const IVY = [];                                     // the leaves that can flip: { x, y, ph, c }
let IVY_REC = false;                                // record them while the static layers are painted
const IVYR = ['#08130c', '#0e2214', '#16321c', '#204426', '#2c5630'];
/** an ivy leaf's colour at (u, v) metres on a wall, or null where the patch has no leaf; mask: the patch's strength */
function ivyAt(u, v, mask, L, x, y, th) {
  const cu = Math.floor(u / 0.11), cv = Math.floor(v / 0.09), leaf = hash(cu, cv, 42);
  if (mask + (leaf - 0.5) * 0.18 < 0.6) return null;
  const val = clamp(0.18 + leaf * 0.45 + L * 0.35, 0, 1), f = val * (IVYR.length - 1);
  let k = Math.floor(f); if (f - k > th) k++;
  if (IVY_REC && leaf > 0.5) IVY.push({ x, y, ph: u * 0.31 + v * 0.17 + leaf, c: mixHex('#6a8a5a', '#ffe0a0', clamp(L * 0.4, 0, 0.5)) });
  return IVYR[Math.min(k, IVYR.length - 1)];
}
const IVY_FRONT = [[0, 6.95, 8.12, 5.8], [0, 5.2, 6.95, 9.0, 3.05], [1, 12.9, 14.55, 8.9], [2, 20.6, 22.0, 6.2], [1, 9.6, 10.35, 7.5]];   // house, from Z, to Z, up to, from (m)
/** the colour of the fronts at depth Z, height Y (absolute); light: the gas light there; (x, y): the pixel, for dithering */
function frontAt(Z, Y, light, x, y) {
  const hi = HOUSES.indexOf(houseAt(Z)), Ho = HOUSES[hi], hh = Y - baseOf(Ho), zr = Z - Ho.z0, L = clamp(light, 0, 1.4);
  const near = clamp((16 - Z) / 8, 0, 1);                                              // brick courses and joints show up close only
  const dd = (x + 1) & 3, ee = (y + 2) & 3, th = bayer(dd, ee);
  const rampV = (ramp, v) => { const f = clamp(v, 0, 1) * (ramp.length - 1); let k = Math.floor(f); if (f - k > th) k++; return ramp[Math.min(k, ramp.length - 1)]; };
  const corn = Ho.corn;
  // above the cornice: a snowy parapet, dormers or a slate mansard; else open sky
  if (hh > corn + 0.12) {
    if (Ho.attic === 'flat') return hh < corn + 0.7 ? (hh > corn + 0.56 ? rampV(SNOWC, 0.55 + L * 0.3) : rampV(BRICKS[Ho.brick], 0.22 + L * 0.3)) : null;
    if (Ho.attic === 'mansard') {
      if (hh > corn + 2.3) return null;
      if (hh > corn + 2.12) return rampV(SNOWC, 0.6);
      const ww = WINDOWS(Ho).upper.some(c => Math.abs(Z - c) < 0.45) && hh > corn + 0.6 && hh < corn + 1.8;
      if (ww) return winLit(hi, 4, 0) ? rampV(WIN_LIT, 0.45) : rampV(WIN_DARK, 0.5);
      return rampV(['#1e1e2c', '#2a2a3c', '#3c3c52', '#56566e'], 0.35 + (frac(hh * 3) < 0.2 ? -0.15 : 0) + (frac(hh * 3) > 0.8 ? 0.35 : 0));
    }
    const dm = WINDOWS(Ho).upper[1];                                                    // one dormer over the middle bay
    if (Math.abs(Z - dm) < 0.75 && hh < corn + 1.9) {
      if (hh > corn + 1.72) return rampV(SNOWC, 0.6);
      if (Math.abs(Z - dm) < 0.4 && hh > corn + 0.55 && hh < corn + 1.5) return winLit(hi, 5, 0) ? rampV(WIN_LIT, 0.5) : rampV(WIN_DARK, 0.4);
      return rampV(['#2a2a3a', '#3e3e52', '#5a5a70'], 0.4);
    }
    return null;
  }
  if (hh > corn - 0.38) {                                                               // the cornice: a painted wooden crown, snow on top
    if (hh > corn + 0.02) return rampV(SNOWC, 0.62 + L * 0.3);
    if (hh > corn - 0.12) return rampV(['#3a3640', '#5a5660', '#8a8690', '#b8b4bc'], 0.45 + L * 0.4);
    return (frac(Z * 4) < 0.45 && near > 0.2) ? rampV(['#1e1a22', '#2e2a32', '#4a4650'], 0.4 + L * 0.3) : rampV(['#2e2a34', '#4a4650', '#6e6a74', '#9a96a0'], 0.35 + L * 0.4);
  }
  if (Math.abs(zr) < 0.07 || Math.abs(Z - Ho.z1) < 0.07) return '#120a0c';               // a party wall's joint and its downpipe
  for (const [ih, z0, z1, top, bot = 0.5] of IVY_FRONT) if (ih === hi && Z > z0 && Z < z1 && hh > bot && hh < top) {
    const m = 0.62 + 0.3 * Math.min((Z - z0) / 0.25, (z1 - Z) / 0.25, (top - hh) / 0.6, (hh - bot) / 0.4, 1) - 0.25 + vnoise(Z * 2.2, hh * 1.4, 43) * 0.25;
    const c = ivyAt(Z, hh, m, L, x, y, th);
    if (c) return c;
  }
  // the door, its surround and fanlight
  const d0 = Ho.door, d1 = Ho.door + DOOR_W;
  if (Z > d0 - 0.24 && Z < d1 + 0.24 && hh > 0.5 && hh < 3.62) {
    if (hh > 3.38 || Z < d0 || Z > d1) return rampV(GRANITE, (hh > 3.5 ? 0.85 : 0.5) + L * 0.4);   // lintel and jambs
    if (hh > 2.78) {                                                                    // the fanlight: lit, its leading radiating
      const u = (Z - d0) / DOOR_W, spoke = Math.abs(frac(Math.atan2(hh - 2.78, (u - 0.5) * 1.1) / Math.PI * 7) - 0.5) < 0.12;
      return spoke && near > 0.3 ? '#4a3018' : rampV(WIN_LIT, 0.6 + 0.3 * (1 - Math.abs(u - 0.5)));
    }
    const D = DOORS[Ho.col], u = (Z - d0) / DOOR_W;
    if (hh > 1.55 && hh < 2.25 && Math.hypot((u - 0.5) * DOOR_W, hh - 1.9) < 0.27 && Math.hypot((u - 0.5) * DOOR_W, hh - 1.9) > 0.13) return hash(Math.round(Z * 40), Math.round(hh * 40), 3) < 0.18 ? '#c8282a' : '#183a20';   // the wreath
    if (Math.abs(u - 0.5) < 0.05 && Math.abs(hh - 1.62) < 0.05) return '#d8b050';         // the brass knocker
    if (Math.abs(u - 0.82) < 0.05 && Math.abs(hh - 1.15) < 0.04) return '#c8a040';         // and the knob
    const panel = (Math.abs(u - 0.5) < 0.06 || frac((hh - 0.55) / 0.55) < 0.12) && near > 0.25;
    return rampV(D, (panel ? 0.15 : 0.55) + L * 0.35 + (u > 0.85 ? -0.2 : 0));
  }
  // the windows, floor by floor: shutters, granite lintels and sills, six-over-six sashes, a candle
  const W_ = WINDOWS(Ho);
  for (let f = 0; f < Ho.st; f++) {
    const [s0, s1] = FLOORS[f];
    if (hh < s0 - 0.12 || hh > s1 + 0.2) continue;
    const bays = f ? W_.upper : W_.ground;
    for (let b = 0; b < bays.length; b++) {
      const c = bays[b], dz = Z - c;
      if (Math.abs(dz) > 0.98) continue;
      if (hh > s1) return Math.abs(dz) < 0.62 ? rampV(GRANITE, 0.55 + L * 0.4) : rampV(BRICKS[Ho.brick], 0.3 + L * 0.5);
      if (hh < s0) return Math.abs(dz) < 0.6 ? rampV(SNOWC, hh > s0 - 0.05 ? 0.7 + L * 0.25 : 0.35) : rampV(BRICKS[Ho.brick], 0.3 + L * 0.5);
      if (Math.abs(dz) > 0.52) return rampV(SHUT, 0.3 + L * 0.5 + (frac(hh * 9) < 0.35 && near > 0.3 ? -0.2 : 0));   // a shutter
      if (Math.abs(dz) > 0.46) return '#2a221e';                                         // the frame
      const lit = winLit(hi, f, b), v = (hh - s0) / (s1 - s0);
      if (lit && f < 2 && Math.abs(dz) < 0.05 && v > 0.02 && v < 0.2) return v > 0.14 ? '#fff2b0' : '#f0ece4';   // the candle on the sill
      const mullion = Math.abs(dz) < 0.035 || Math.abs(v - 0.5) < 0.03 || (near > 0.4 && (Math.abs(frac(v * 3) - 0.5) > 0.47 || Math.abs(Math.abs(dz) - 0.25) < 0.02));
      if (mullion) return lit ? '#5a3a1e' : '#0a0a10';
      return lit ? rampV(WIN_LIT, 0.35 + v * 0.45 + (hash(hi, f * 3 + b, 9) - 0.5) * 0.2) : rampV(WIN_DARK, 0.25 + v * 0.3 + L * 0.3);
    }
  }
  if (hh < 0.52) return rampV(GRANITE, 0.32 + L * 0.45 + (near > 0.3 && frac(Z / 1.2) < 0.03 ? -0.2 : 0));   // the granite base course
  // brick: Flemish-ish courses, each brick its own shade, joints close up; lit by the lamps, cool away from them
  const course = Math.floor(hh / 0.077), joint = near * (frac(hh / 0.077) < 0.2 ? 1 : 0) + near * (frac((Z + (course & 1) * 0.1) / 0.2) < 0.12 ? 1 : 0);
  const bv = (hash(course, Math.floor((Z + (course & 1) * 0.1) / 0.2), 11 + hi) - 0.5) * 0.18 * near;
  return rampV(BRICKS[Ho.brick], 0.24 + bv + L * 0.62 - (joint ? 0.18 : 0) - 0.05 * smooth(4, 10, hh));
}
/** the fronts' canvas: every column back to its depth, every row to its height; region limits the painted pixels */
function renderFront(region) {
  const p = pix(W, H);
  IVY_REC = !region;
  const [rx0, ry0, rx1, ry1] = region || [0, 0, W, H];
  for (let x = Math.max(0, rx0); x < Math.min(rx1, Math.ceil(sx(XL, ZEND))); x++) {
    const Za = zOfL(x - 0.5), Zb = Math.min(ZEND, zOfL(x + 0.5));
    if (Za >= ZEND) break;
    const Zc = Math.min(ZEND - 0.01, zOfL(x));
    const yTop = Math.max(0, Math.floor(syG(houseAt(Zc).corn + 2.6, Zc))), yBot = Math.min(H - 1, Math.ceil(sy(gY(Zc), Zc)));
    for (let y = Math.max(ry0, yTop); y <= Math.min(ry1 - 1, yBot); y++) {
      const Z = Math.min(ZEND - 0.01, lerp(Za, Zb, colU(x)));
      const Y = EYE - (y + 0.5 - HY) * Z / F;
      if (Y < gY(Z)) continue;
      const light = lampAt(XL + 0.05, Y, Z) + 0.06;
      const c = frontAt(Z, Y, light, x, y);
      if (c) p.set(x, y, c);
    }
  }
  IVY_REC = false;
  return p;
}
// windows that switch on and off over the loop: [house, floor, bay, cycles a loop, phase, duty]
const TOGGLE_DEF = [[1, 2, 0, 3, 0.2, 0.6], [2, 3, 1, 2, 0.55, 0.5], [3, 1, 2, 4, 0.1, 0.65], [4, 2, 1, 3, 0.7, 0.55],
  [5, 1, 0, 2, 0.35, 0.7], [6, 2, 2, 5, 0.8, 0.6], [7, 1, 1, 3, 0.45, 0.6], [8, 2, 0, 4, 0.15, 0.5], [9, 1, 2, 2, 0.6, 0.7],
  [0, 1, 1, 2, 0.9, 0.6], [1, 1, 0, 3, 0.62, 0.75], [2, 0, 1, 2, 0.05, 0.8], [2, 1, 1, 4, 0.37, 0.7], [2, 1, 2, 3, 0.81, 0.65],
  [2, 2, 1, 2, 0.27, 0.75], [2, 2, 2, 5, 0.5, 0.7], [3, 0, 0, 3, 0.93, 0.8], [3, 1, 0, 2, 0.14, 0.7], [3, 2, 0, 4, 0.66, 0.6],
  [3, 2, 2, 3, 0.42, 0.75], [4, 1, 2, 2, 0.71, 0.7], [4, 2, 0, 5, 0.2, 0.65], [5, 2, 1, 3, 0.57, 0.7], [6, 1, 0, 4, 0.86, 0.7],
  [7, 2, 2, 2, 0.33, 0.6], [8, 1, 1, 3, 0.09, 0.7], [9, 2, 0, 4, 0.48, 0.65],
  [4, 0, 1, 3, 0.15, 0.8], [5, 1, 2, 4, 0.74, 0.7], [5, 2, 0, 2, 0.29, 0.65], [6, 0, 0, 3, 0.61, 0.8], [6, 1, 2, 2, 0.04, 0.7],
  [6, 2, 1, 4, 0.39, 0.6], [7, 1, 0, 3, 0.83, 0.7], [7, 2, 0, 5, 0.12, 0.65], [7, 3, 1, 2, 0.58, 0.6], [8, 0, 1, 4, 0.26, 0.8],
  [8, 2, 2, 3, 0.69, 0.6], [9, 1, 0, 5, 0.95, 0.7], [9, 2, 2, 2, 0.21, 0.65], [4, 2, 2, 4, 0.52, 0.6], [2, 0, 0, 4, 0.88, 0.75]];
/** the screen box of a window (house hi, floor f, bay b) on the fronts, a pixel of slack */
function winBox(hi, f, b) {
  const Ho = HOUSES[hi], c = (f ? WINDOWS(Ho).upper : WINDOWS(Ho).ground)[b], [s0, s1] = FLOORS[f], base = baseOf(Ho);
  const xa = Math.floor(sx(XL, c - 0.6)) - 1, xb = Math.ceil(sx(XL, c + 0.6)) + 1;
  return [xa, Math.floor(sy(base + s1 + 0.1, c - 0.6)) - 1, xb, Math.ceil(sy(base + s0 - 0.1, c - 0.6)) + 2];
}

/* ================= The north side: the garden walls, the corner house at Willow Street ================= */
const PIERS = [12.4, 24.6, 31.0, 43.2, 52.6];
function wallAt(Z, Y, light, x, y) {
  const L = clamp(light, 0, 1.4), dd = (x + 3) & 3, ee = (y + 1) & 3, th = bayer(dd, ee);
  const rampV = (ramp, v) => { const f = clamp(v, 0, 1) * (ramp.length - 1); let k = Math.floor(f); if (f - k > th) k++; return ramp[Math.min(k, ramp.length - 1)]; };
  const hh = Y - gY(Z), near = clamp((14 - Z) / 7, 0, 1);
  if (Z < NEAR_R.z1) {                                                                 // the corner house's side: brick, a lit window's edge
    if (hh < 0.5) return rampV(GRANITE, 0.3 + L * 0.4);
    if (hh > 1.3 && hh < 2.9 && Z < 5.05) return Z > 4.95 ? '#2a221e' : rampV(WIN_LIT, 0.45 + (hh - 1.3) * 0.25);
    if (hh > 1.3 && hh < 2.9 && Z < 5.5) return rampV(SHUT, 0.25 + L * 0.4);
    const course = Math.floor(hh / 0.077), joint = frac(hh / 0.077) < 0.22 || frac((Z + (course & 1) * 0.1) / 0.2) < 0.1;
    return rampV(BRICKS[3], 0.2 + L * 0.55 - (joint ? 0.12 : 0) + (hash(course, Math.floor(Z * 5), 5) - 0.5) * 0.12);
  }
  const pier = PIERS.some(p => Z > p && Z < p + 0.5), top = GARDEN.top + (pier ? 0.28 : 0) + 0.12 * Math.sin(Z * 0.7);
  if (hh > top) return null;
  if (hh > top - 0.06) return rampV(SNOWC, 0.62 + L * 0.3);                            // snow on the coping
  if (hh > top - 0.2) return rampV(GRANITE, 0.45 + L * 0.4);                           // the coping stones
  if (Z > GDOOR.z0 - 0.15 && Z < GDOOR.z1 + 0.15 && hh < 2.45 + 0.2 * Math.sin(Math.PI * clamp((Z - GDOOR.z0) / (GDOOR.z1 - GDOOR.z0), 0, 1))) {
    if (Z < GDOOR.z0 || Z > GDOOR.z1) return rampV(GRANITE, 0.5 + L * 0.3);
    const u = (Z - GDOOR.z0) / (GDOOR.z1 - GDOOR.z0);
    if (Math.hypot((u - 0.5) * 1.1, hh - 1.6) < 0.24 && Math.hypot((u - 0.5) * 1.1, hh - 1.6) > 0.12) return '#1a3e22';   // a wreath on the garden door
    return rampV(DOORS.green, 0.3 + L * 0.5 + (frac(u * 5) < 0.15 ? -0.25 : 0) + (Math.abs(hh - 0.6) < 0.05 || Math.abs(hh - 1.9) < 0.05 ? -0.3 : 0));
  }
  if (hh < 0.32) return rampV(GRANITE, 0.28 + L * 0.4);
  if (!pier && Z > 6.0 && Z < 34 && hh > 0.4 && hh < top - 0.24) {                    // ivy over the garden walls, in patches
    const m = vnoise(Z / 2.6, hh / 1.7, 41) * 0.8 + 0.16 + 0.08 * Math.min(1, (top - 0.24 - hh) / 0.4) - 0.08 - 0.12 * smooth(20, 34, Z);
    const c = ivyAt(Z, hh, m, L, x, y, th);
    if (c) return c;
  }
  const soot = vnoise(Z / 3.2, hh / 1.4, 21) > 0.68 ? 0.05 : 0;                         // weathering: a sootier patch here and there
  const course = Math.floor(hh / 0.077), joint = near * (frac(hh / 0.077) < 0.22 ? 1 : 0) + near * (frac((Z + (course & 1) * 0.1) / 0.2) < 0.12 ? 1 : 0);
  return rampV(BRICKS[pier ? 2 : 1], 0.24 - soot + L * 0.6 - (joint ? 0.16 : 0) + (hash(course, Math.floor(Z * 5), 6) - 0.5) * 0.14 * near - (pier ? 0.06 : 0));
}
function renderWall() {
  const p = pix(W, H);
  IVY_REC = true;
  for (let x = Math.floor(sx(XR, CORNER.Z)); x < W; x++) {
    const Za = Math.min(CORNER.Z, zOfR(x - 0.5)), Zb = zOfR(x + 0.5), Zc = zOfR(x);
    if (Zc > CORNER.Z) continue;
    const yTop = Math.max(0, Math.floor(syG(Zc < NEAR_R.z1 ? 14 : GARDEN.top + 0.6, Zc))), yBot = Math.min(H - 1, Math.ceil(sy(gY(Zc), Zc)));
    for (let y = yTop; y <= yBot; y++) {
      const Z = lerp(Zb, Za, colU(x)), Y = EYE - (y + 0.5 - HY) * Z / F;
      if (Y < gY(Z)) continue;
      const c = wallAt(Z, Y, lampAt(XR - 0.05, Y, Z) + 0.05, x, y);
      if (c) p.set(x, y, c);
    }
  }
  IVY_REC = false;
  return trim(p);
}

/* ================= The lane: brick sidewalks, granite curbs, river-stone cobbles, the snow on them ================= */
// Snow lies thin: on the cobbles' crowns with their joints dark between, on the curbs, on the sidewalks where nobody
// has shovelled; the lamps lay warm pools on it, cool lilac away from them. Feet have trodden a path down the middle.
// one ramp for the whole lane, cool dark joints to warm lamplit snow: stone, brick and snow are told apart by value
const GRAMP = ['#0c0b12', '#16151f', '#22202c', '#302e3c', '#403e50', '#545268', '#6a6880', '#848098', '#a09aae', '#bcb2bc',
  '#d4c6c2', '#e8d6c4', '#f6e4c8', '#fff0d4'];
const BRAMP = ['#160e12', '#22141a', '#30191c', '#422220', '#562e28', '#6c3c30', '#84503c'];   // bare brick, where it's swept
const CLEARED = [{ X0: XL, X1: XCL - 0.18, Z0: 33.6, Z1: 37.4 }];   // the shoveller's stretch of sidewalk
/** the lane's colour at a point; dz: how many metres of depth the pixel spans (detail fades out past a stone's depth) */
function groundAt(X, Z, light, x, y, dz) {
  const L = clamp(light, 0, 1.5), th = bayer((x + 2) & 3, (y + 3) & 3);
  const rampV = (ramp, v) => { const f = clamp(v, 0, 1) * (ramp.length - 1); let k = Math.floor(f); if (f - k > th) k++; return ramp[Math.min(k, ramp.length - 1)]; };
  const lit = v => rampV(GRAMP, v + L * 0.36);
  const detail = clamp((0.3 - dz) / 0.18, 0, 1), low = (vnoise(X * 1.4, Z * 0.6, 35) - 0.5) * 0.08;
  if (X < XCL - 0.17 || X > XCR + 0.15) {                                              // a brick sidewalk under thin snow
    const left = X < 0, cleared = CLEARED.some(c => X > c.X0 && X < c.X1 && Z > c.Z0 && Z < c.Z1);
    const bz = Math.floor(Z / 0.2), joint = detail > 0.3 && (frac(Z / 0.2) < 0.14 || frac((X + (bz & 1) * 0.05) / 0.1) < 0.14);
    if (cleared) return joint ? '#140c10' : rampV(BRAMP, 0.25 + L * 0.42 + (hash(Math.floor((X + (bz & 1) * 0.05) / 0.1), bz, 2) - 0.5) * 0.2 * detail);
    const drift = vnoise(X * 3, Z * 1.3, left ? 31 : 32), prints = left && vnoise(X * 7, Z * 1.6, 33) > 0.7;
    return lit(0.5 + (left ? 0 : 0.06) + drift * 0.12 - (prints ? 0.1 : 0) - (joint ? 0.06 : 0) + low);
  }
  if (X < XCL || X > XCR) {                                                            // the granite curbs, snow along them, a shadow under
    const edge = (X > XCL - 0.035 && X < XCL) || (X < XCR + 0.035 && X > XCR);
    return edge ? rampV(GRAMP, 0.12 + L * 0.2) : lit(0.6 + low);
  }
  // the cobbles: rounded river stones in loose rows, snow on their crowns; a trodden path down the middle
  const trod = Math.exp(-(((X - 0.2) / 0.6) ** 2)) * 0.55;
  const avg = 0.46 - trod * 0.12 + low;
  if (detail <= 0) return lit(avg);
  const row = Math.floor(Z / 0.25), off = (row & 1) * 0.1 + hash(row, 1, 1) * 0.05;
  const col = Math.floor((X + off) / 0.2);
  const cx = (col + 0.5 + (hash(row, col, 2) - 0.5) * 0.25) * 0.2 - off, cz = (row + 0.5 + (hash(row, col, 3) - 0.5) * 0.2) * 0.25;
  const d = ((X - cx) / (0.088 + hash(row, col, 4) * 0.012)) ** 2 + ((Z - cz) / (0.11 + hash(row, col, 5) * 0.015)) ** 2;
  const cap = 0.58 - trod * 0.4 + (hash(row, col, 6) - 0.5) * 0.25;
  let v;
  if (d > 1) v = hash(row, col, 7) < 0.3 - trod * 0.2 ? 0.44 : 0.16;                   // a joint, some filled with snow
  else if (d < cap) v = 0.6 + (1 - d / cap) * 0.08 - trod * 0.14;                      // snow on the crown
  else v = 0.3 + (Z < cz ? 0.06 : -0.04) + (hash(row, col, 8) - 0.5) * 0.08;           // the stone's flank
  return lit(lerp(avg, v, detail));
}
function renderGround() {
  const p = pix(W, H), y0 = Math.ceil(sy(gY(ZEND), ZEND) - 0.5);
  for (let y = y0; y < H; y++) {
    const Za = zOfG(y + 0.5), Zb = Math.min(ZEND, zOfG(y - 0.5)), Z = lerp(Za, Zb, colU(y + 7)), dz = Zb - Za;
    const xa = Math.max(0, Math.floor(sx(XL, Z)) - 1), xb = Math.min(W - 1, Math.ceil(sx(XR, Z)) + 1);
    for (let x = xa; x <= xb; x++) {
      const X = CAMX + (x + 0.5 - VX) * Z / F;
      if (X < XL || X > XR) continue;
      const c = groundAt(X, Z, lampAt(X, gY(Z) + 0.05, Z), x, y, dz);
      if (c) p.set(x, y, c);
    }
  }
  return trim(p);
}

/* ================= On the fronts: steps, railings, window boxes, door lanterns, the balcony ================= */
const SPRIGS = [];                                  // the near window boxes' sprigs: { a, b, col, tip, berry, ph }
function drawSprigs(t) {
  const f = flurry(t);
  for (const S of SPRIGS) {
    const sw = Math.round((wave(t, 37, S.ph) * 0.8 + wave(t, 89, S.ph * 2) * 0.4) * (1 + 1.5 * f)), x = Math.round(S.b[0]) + sw, y0 = Math.round(S.b[1]), y1 = Math.round(S.a[1]);
    ctx.fillStyle = S.col; ctx.fillRect(x, y0, 1, Math.max(1, y1 - y0 + 1));
    ctx.fillStyle = S.tip; ctx.fillRect(x, y0 - 1, 1, 1);
  }
}
const IRON = '#0c0c12', IRON_L = '#2a2a34';
function stepsOf(Ho) {                                                                 // the stoop: risers up to the door
  const base = baseOf(Ho), g = gY(Ho.door), rise = base + 0.55 - g, n = Math.max(2, Math.ceil(rise / 0.19));
  return { n, rise: rise / n, g, d0: Ho.door - 0.22, d1: Ho.door + DOOR_W + 0.22, top: base + 0.55 };
}
function paintProps(p) {
  HOUSES.forEach((Ho, hi) => {
    const { n, rise, g, d0, d1, top } = stepsOf(Ho);
    for (let k = 0; k < n; k++) {                                                      // treads and risers, top step first
      const yT = top - k * rise, xo = XL + 0.3 * k, xi = XL + 0.3 * (k + 1);
      const Lz = lampAt(xi, yT, d0);
      fillPoly(p, proj([[xi, yT, d0], [xi, yT, d1], [xi, yT - rise, d1], [xi, yT - rise, d0]]), (x, y) => pick(GRANITE, 0.3 + Lz * 0.4, x, y));
      fillPoly(p, proj([[xo, yT, d0], [xo, yT, d1], [xi, yT, d1], [xi, yT, d0]]), (x, y) => pick(SNOWC, 0.62 + Lz * 0.3, x, y));
    }
    const Ls = lampAt(XL + 0.4, top, d0);                                              // the stoop's near side, square on
    for (let k = 0; k < n; k++) fillPoly(p, proj([[XL + 0.3 * k, g, d0], [XL + 0.3 * k, top - k * rise, d0], [XL + 0.3 * (k + 1), top - k * rise, d0],
      [XL + 0.3 * (k + 1), g, d0]]), (x, y) => pick(GRANITE, 0.42 + Ls * 0.4 + (x % 3 === 0 ? -0.06 : 0), x, y));
    for (let k = 0; k < n; k++) { const [a, b] = [proj([[XL + 0.3 * k, top - k * rise, d0]])[0], proj([[XL + 0.3 * (k + 1), top - k * rise, d0]])[0]]; p.line(a[0], a[1], b[0], b[1], '#e8e2e8'); }
    const s = pxm(d0);                                                                 // the iron railing along the near side, a scroll at its foot
    if (s > 9) {
      const hA = proj([[XL, top + 0.85, d0 - 0.02]])[0], hB = proj([[XL + 0.3 * n, top - (n - 1) * rise + 0.8, d0 - 0.02]])[0];
      p.line(hA[0], hA[1], hB[0], hB[1], IRON);
      for (let k = 0; k <= n; k++) {
        const X = XL + 0.3 * k, yT = top - Math.min(k, n - 1) * rise, a = proj([[X, yT, d0 - 0.02]])[0], b = proj([[X, yT + 0.82 + (k === n ? -0.02 : 0), d0 - 0.02]])[0];
        p.line(a[0], a[1], b[0], b[1], k === n ? IRON_L : IRON);
      }
      const sc = proj([[XL + 0.3 * n + 0.08, g + 0.12, d0 - 0.06]])[0];                // the boot scraper
      if (s > 14) { p.line(sc[0] - 2, sc[1], sc[0] - 2, sc[1] - 3, IRON); p.line(sc[0] + 2, sc[1], sc[0] + 2, sc[1] - 3, IRON); p.line(sc[0] - 2, sc[1] - 3, sc[0] + 2, sc[1] - 3, IRON_L); }
    } else if (s > 3) {
      const a = proj([[XL + 0.3 * n, g, d0]])[0], b = proj([[XL + 0.3 * n, top - (n - 1) * rise + 0.8, d0]])[0];
      p.line(a[0], a[1], b[0], b[1], IRON);
    }
    for (const c of WINDOWS(Ho).ground) {                                              // window boxes: evergreens, berries, snow
      const base = baseOf(Ho), z0 = c - 0.55, z1 = c + 0.55, yb = base + 1.02, yt = base + 1.22, xo = XL + 0.26;
      const Lw = lampAt(xo, yt, c);
      fillPoly(p, proj([[xo, yt, z0], [xo, yt, z1], [xo, yb, z1], [xo, yb, z0]]), (x, y) => pick(['#0e1012', '#1a1e1e', '#2a3030'], 0.3 + Lw * 0.4, x, y));
      fillPoly(p, proj([[XL, yb, z0], [XL, yt, z0], [xo, yt, z0], [xo, yb, z0]]), (x, y) => pick(['#121618', '#222a2a', '#364040'], 0.4 + Lw * 0.4, x, y));
      const sp2 = pxm(c), r = rng(hi * 13 + Math.round(c * 7));
      const nSprig = Math.max(3, Math.round(sp2 * 0.9));
      for (let i = 0; i < nSprig; i++) {                                               // sprigs over the box's top
        const zz = lerp(z0 + 0.05, z1 - 0.05, r()), xx = lerp(XL + 0.04, xo, r()), hgt = 0.12 + r() * 0.16;
        const a = proj([[xx, yt, zz]])[0], b = proj([[xx, yt + hgt, zz]])[0], col = r() < 0.5 ? '#14301c' : '#1e4226', tip = r() < 0.55 ? '#dcd8e4' : '#1e4226', berry = r() < 0.3;
        if (sp2 > 34) {                                                                // close by, their tips stir in the wind (drawn per frame)
          const m = [lerp(a[0], b[0], 0.7), lerp(a[1], b[1], 0.7)];
          p.line(a[0], a[1], m[0], m[1], col); if (berry) p.set(a[0] + 1, a[1] - 1, '#c02a26');
          SPRIGS.push({ a: m, b, col, tip, ph: r() }); continue;
        }
        p.line(a[0], a[1], b[0], b[1], col);
        p.set(b[0], b[1] - 1, tip);
        if (berry) p.set(a[0] + 1, a[1] - 1, '#c02a26');
      }
    }
    if (Ho.balcony) {                                                                  // the cast-iron balcony on the second floor
      const base = baseOf(Ho), z0 = Ho.z0 + 0.5, z1 = Ho.z1 - 0.5, yF = base + 3.82, xo = XL + 0.62;
      fillPoly(p, proj([[XL, yF, z0], [XL, yF, z1], [xo, yF, z1], [xo, yF, z0]]), (x, y) => pick(SNOWC, 0.5, x, y));
      fillPoly(p, proj([[xo, yF + 0.95, z0], [xo, yF + 0.95, z1], [xo, yF, z1], [xo, yF, z0]]), (x, y) => (x + y) % 2 ? IRON : null);
      const pts = proj([[XL, yF, z0], [XL, yF + 0.95, z0], [xo, yF + 0.95, z0], [xo, yF, z0]]);
      fillPoly(p, pts, (x, y) => (x % 2 === 0 || y === Math.round(pts[1][1]) || y === Math.round(pts[0][1]) - 1) ? IRON : null);
      const a = proj([[XL, yF + 0.97, z0]])[0], b = proj([[xo, yF + 0.97, z0]])[0], c2 = proj([[xo, yF + 0.97, z1]])[0];
      p.line(a[0], a[1], b[0], b[1], '#d8d4e0'); p.line(b[0], b[1], c2[0], c2[1], '#b8b4c4');
    }
  });
  for (const Lt of LANTERNS) paintLantern(p, Lt.X, Lt.Y, Lt.Z, true);
}
/** a gas lantern: copper hood under snow, four lit panes, a frame; on a wall bracket (wall) or a post's top */
function paintLantern(p, X, Y, Z, wall) {
  const s = pxm(Z), cx = sx(X, Z), cy = sy(Y, Z);
  const w = Math.max(2, Math.round(0.3 * s)), h = Math.max(3, Math.round(0.42 * s)), hood = Math.max(1, Math.round(0.14 * s));
  const x0 = Math.round(cx - w / 2), y0 = Math.round(cy - h / 2);
  if (wall) { const xw = sx(XL, Z); p.line(xw, y0 + h, x0 + (w >> 1), y0 + h, IRON); p.line(xw, y0 + h - Math.max(1, Math.round(s * 0.18)), x0, y0 + h, IRON); }
  for (let k = 0; k < hood; k++) for (let x = x0 - 1 + Math.round(k * (w / 2) / hood); x <= x0 + w - Math.round(k * (w / 2) / hood); x++) p.set(x, y0 - 1 - k, k === hood - 1 || hood === 1 ? '#e4e0ec' : '#2a3a30');
  for (let y = y0; y < y0 + h; y++) for (let x = x0; x < x0 + w; x++) {
    const edge = x === x0 || x === x0 + w - 1 || (w > 5 && x === x0 + (w >> 1));
    p.set(x, y, edge ? '#3a3020' : y < y0 + h * 0.45 ? '#fff6dc' : '#ffe2a0');
  }
  for (let x = x0; x < x0 + w; x++) p.set(x, y0 + h, '#1a1a1e');
  if (s > 12) p.set(x0 + (w >> 1), y0 + h + 1, '#1a1a1e');
}

/* ================= The gas lamp posts ================= */
// A black cast-iron post on a fluted base, the ladder bar under the lantern, a four-paned copper lantern with a snowy
// hood; the nearest wound with a garland and a red bow. Each is its own sprite, drawn in feet order with the people.
function renderPost(P) {
  const p = pix(W, H), s = pxm(P.Z), g = gY(P.Z), cx = Math.round(sx(P.X, P.Z));
  const yAt = h => Math.round(sy(g + h, P.Z));
  const pw = Math.max(1, Math.round(0.11 * s)), bw = Math.max(1, Math.round(0.24 * s));
  for (let y = yAt(2.66); y <= yAt(0); y++) {
    const h = (yAt(0) - y) / s, w = h < 0.5 ? bw : h < 0.7 ? Math.round(lerp(bw, pw, (h - 0.5) / 0.2)) : pw;
    for (let x = cx - (w >> 1); x < cx - (w >> 1) + w; x++) p.set(x, y, x === cx - (w >> 1) && w > 1 ? IRON_L : IRON);
  }
  if (s > 6) {                                                                          // the ladder bar, just under the lantern's neck
    const y = yAt(2.48), a = Math.round(0.3 * s);
    p.line(cx - a, y, cx + a, y, IRON); p.set(cx - a, y - 1, '#d8d4e0'); p.set(cx + a, y - 1, '#d8d4e0');
  }
  const lw = Math.max(2, Math.round(0.42 * s)), lh = Math.max(3, Math.round(0.6 * s));    // the post lantern is bigger than a door's
  const x0 = cx - (lw >> 1), y0 = yAt(POST_Y + 0.3);
  for (let y = y0; y < y0 + lh; y++) for (let x = x0; x < x0 + lw; x++) {
    const edge = x === x0 || x === x0 + lw - 1 || (lw > 6 && x === cx);
    p.set(x, y, edge ? '#3a3020' : y < y0 + lh * 0.45 ? '#fff8e4' : '#ffe6a8');
  }
  const hood = Math.max(1, Math.round(0.18 * s));
  for (let k = 0; k < hood; k++) for (let x = x0 - 1 + Math.round(k * lw / 2 / hood); x <= x0 + lw - Math.round(k * lw / 2 / hood); x++) p.set(x, y0 - 1 - k, k === hood - 1 ? '#ece8f2' : k === hood - 2 ? '#c8c4d4' : '#24342c');
  p.set(cx, y0 - hood - 1, IRON); if (s > 10) p.set(cx, y0 - hood - 2, IRON);
  for (let x = x0; x < x0 + lw; x++) p.set(x, y0 + lh, IRON);
  // the lantern's tapered neck, from its base down onto the post's top, ringed by a collar where it meets the post:
  // the lantern stands on its post at every depth, with no sky between
  const nTop = y0 + lh + 1, nBot = Math.max(nTop, yAt(2.58)), nw0 = Math.max(pw, Math.round(lw * 0.55));
  for (let y = nTop; y <= nBot; y++) {
    const k = nBot > nTop ? (y - nTop) / (nBot - nTop) : 1, collar = s > 20 && y === nBot;
    const w = collar ? pw + 2 : Math.max(pw, Math.round(lerp(nw0, pw, Math.sqrt(k)))), xa = cx - (w >> 1);
    for (let x = xa; x < xa + w; x++) p.set(x, y, x === xa && w > 1 ? IRON_L : IRON);
  }
  if (P.garland) {                                                                      // the garland's spiral and the red bow
    for (let h = 0.4; h < 2.62; h += 0.012) {
      const a = h * 9, xx = cx + Math.cos(a) * (pw * 0.5 + 1.2), yy = sy(g + h, P.Z);
      if (Math.sin(a) > -0.2) p.set(xx, yy, hash(Math.round(h * 300), 2) < 0.12 ? '#c8282a' : Math.sin(a) > 0.5 ? '#2a5a32' : '#1a3a22');
    }
    const by = yAt(2.55);
    for (const [dx, dy] of [[-3, -1], [-2, -2], [-2, 0], [-1, -1], [0, -1], [1, -1], [2, -2], [2, 0], [3, -1], [-1, 1], [-1, 2], [1, 1], [1, 2], [-2, 3], [2, 3]]) p.set(cx + dx, by + dy, dy > 0 ? '#9a1a1e' : '#d8302e');
  }
  return trim(p);
}
const postTop = P => ({ x: sx(P.X, P.Z), y: sy(gY(P.Z) + POST_Y, P.Z), s: pxm(P.Z) });

/* ================= The snow: three depths, lit where it passes a lamp; the flurry thickens and slants it ================= */
// [count, flurry's extra, cycles a loop min..max, size, colour, alpha, sway, drift (px over a fall), landing rows]
const SNOW_L = [
  { count: 210, extra: 270, nMin: 13, nMax: 19, size: 1, col: '#9c9cba', a: 0.72, amp: 2, drift: -8, land: [150, 188], lit: false },
  { count: 150, extra: 260, nMin: 21, nMax: 29, size: 1, col: '#dcdcf0', a: 0.92, amp: 3.5, drift: -16, land: [186, 242], lit: true },
  { count: 56, extra: 210, nMin: 33, nMax: 43, size: 2, col: '#f0f0fc', a: 0.95, amp: 7, drift: -28, land: [H + 4, H + 4], lit: true },
];
SNOW_L.forEach((L, li) => {
  const r = rng(8100 + li);
  L.flakes = [];
  for (let i = 0; i < L.count + L.extra; i++) L.flakes.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r(), k: 18 + Math.floor(r() * 40), p: r(), extra: i >= L.count, th: r() });
});
let FLAKE_LIGHTS;                                    // [x, y, rx, ry, colour] on screen, for the lit flakes
const SPAN = W + 60;
/** the wind's push this moment: the flurry comes in from the north-west (the right) and slants everything left */
const windAt = t => 1 + 5 * flurry(t) + 0.8 * flurry(t) * Math.max(0, wave(t, 40));
function drawSnow(L, t) {
  const f = flurry(t), wind = windAt(t);
  for (const fl of L.flakes) {
    let a = L.a;
    if (fl.extra) { const v = (f - fl.th * 0.9) / 0.1; if (v <= 0) continue; a *= Math.min(1, v); }
    const { cyc, u } = cycle(t, fl.n, fl.ph);
    const yEnd = lerp(L.land[0], L.land[1], hash(fl.id, cyc, 2)), y = -6 + u * (yEnd + 6);
    let x = hash(fl.id, cyc, 1) * SPAN + u * L.drift * wind + L.amp * Math.sin(TAU * (fl.k * t / LOOP + fl.p));
    x = ((x % SPAN) + SPAN) % SPAN - 30;
    if (x < -2 || x > W + 1) continue;
    let col = L.col;
    if (L.lit) for (const [cx, cy, rx, ry, lc] of FLAKE_LIGHTS) { const dx = (x - cx) / rx, dy = (y - cy) / ry; if (dx * dx + dy * dy < 1) { col = lc; a = 1; break; } }
    if (L.land[0] < H && u > 0.9) a *= (1 - u) * 10;
    ctx.globalAlpha = a; ctx.fillStyle = col;
    ctx.fillRect(Math.round(x), Math.round(y), L.size, L.size);
  }
  ctx.globalAlpha = 1;
}
// a picked weather (site/js/engine/weather.js): the city's own is this snow; rain and the rest land on the lane at
// the same three depths and catch the same lamps
const WEATHER = weatherPlans(['snow']);
let WX;

/* ================= People ================= */
// One function draws everyone (after boston.js and rome.js), at any size: a look gives the proportions for a height
// and the colours dimmed for the night; person() puts it on its feet. December clothes: wool coats, parkas, scarves,
// hats with bobbles, boots. Lit by the gas lamps, so their shoulders and hats catch a warm line; snow on their hats.
const SKINS = ['#f2c8a0', '#e2ac82', '#c48c60', '#8c5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#d0a858', '#96421c', '#c8c4c0'];
const NIGHT = '#0c1030', GLOWC = '#ffd8a0';
const LOOKS = new Map();
/** a person's look at height h px: d = { skin, hair, coat, legs, hat, scarf, long, coatLen, dk, bobble, top: 'tophat'|'bonnet' } */
function look(d, h) {
  h = Math.max(8, Math.round(h));
  const key = d.id * 100 + h;
  if (LOOKS.has(key)) return LOOKS.get(key);
  const dk = d.dk ?? 0.32, m = c => mixHex(c, NIGHT, dk);
  const hh = Math.max(3, Math.round(h * 0.15)), hw = Math.max(3, Math.round(h * 0.13)), th = Math.max(4, Math.round(h * 0.36));
  const P = { h, hh, hw, th, tw: Math.max(4, Math.round(h * 0.26)), ll: h - hh - th,
    skin: m(SKINS[d.skin]), hair: m(HAIRS[d.hair]), coat: m(d.coat), coatD: mixHex(d.coat, NIGHT, dk + 0.28), coatL: mixHex(d.coat, GLOWC, 0.32),
    legs: m(d.legs), boot: m('#16141a'), hat: d.hat ? m(d.hat) : null, hatL: d.hat ? mixHex(d.hat, GLOWC, 0.3) : null,
    scarf: d.scarf ? m(d.scarf) : null, long: d.long ? Math.max(1, Math.round(h * 0.06)) : 0,
    coatLen: Math.round((d.coatLen ?? 0.2) * (h - hh - th)), bobble: d.bobble ? m(d.bobble) : null, top: d.top || null };
  LOOKS.set(key, P);
  return P;
}
let PID = 1;
const def = (skin, hair, coat, legs, o = {}) => ({ id: PID++, skin, hair, coat, legs, ...o });
const tyOf = (P, fy) => fy - P.ll - P.th + 1;
/**
 * One person, feet on row fy, centred on column x; returns the top row of the head.
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame (0/1, or -1 standing)
 * o.aL / o.aR: 0 down, 1 straight up, 2 up to the face, 3 out in front, 4 up and out, 5 round a neighbour, 6 at the chest
 * o.lean: the head towards a neighbour (px); o.bend: bent forward (profile); o.hunch: shoulders up against the cold
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2, big = P.h >= 22;
  const bend = side && o.bend ? Math.max(1, Math.round(P.h / 14)) : 0, walk = o.step === 0 || o.step === 1;
  const legs = P.ll, ty = fy - legs - th + 1 + bend + (o.hunch ? 1 : 0), hy = ty - hh + (o.hunch ? 1 : 0), bw = side ? Math.max(3, tw - 2) : tw;
  const tx = x - (bw >> 1) + bend * dir;
  // seen square on, a big figure's head is as many pixels short of the shoulders on each side: an even head on odd
  // shoulders (or the other way round) sat half a pixel over, which reads as a head off its body close up; so it takes
  // the width of the other parity nearest its true one
  const hw = !side && big && ((bw - P.hw) & 1) ? P.hw + (P.h * 0.13 > P.hw || P.hw <= 3 ? 1 : -1) : P.hw;
  ctx.fillStyle = 'rgba(10,10,26,0.3)'; ctx.fillRect(x - (bw >> 1) - 1, fy + 1, bw + 2, 1);   // a shadow on the snow
  const ly = fy - legs + 1, lw = Math.max(1, Math.round(bw * 0.38));
  if (side) {
    if (walk) {
      const sp = Math.max(1, Math.round(P.h / 12));
      R(x - 1, ly, 3, Math.max(1, Math.round(legs * 0.3)), P.legs);
      const fwd = o.step ? sp : -sp;
      for (let k = 0; k < legs; k++) { const u = k / legs; R(Math.round(x + fwd * u * dir), ly + k, 2, 1, P.legs); R(Math.round(x - fwd * u * dir), ly + k, 2, 1, mixHex(P.legs, NIGHT, 0.3)); }
      R(Math.round(x + fwd * dir) + (dir > 0 ? 0 : -1), fy, 3, 1, P.boot); R(Math.round(x - fwd * dir), fy, 2, 1, P.boot);
    } else { R(x - 1, ly, 2, legs, P.legs); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.boot); }
  } else {
    const lift = walk ? 1 : 0, a = o.step === 1 ? lift : 0, b = o.step === 0 ? lift : 0;
    R(tx, ly, bw, Math.max(1, Math.round(legs * 0.25)), P.legs);
    R(tx, ly, lw, legs - a, P.legs); R(tx + bw - lw, ly, lw, legs - b, P.legs);
    R(tx, fy - a - (big ? 1 : 0), lw, 1 + (big ? 1 : 0), P.boot); R(tx + bw - lw, fy - b - (big ? 1 : 0), lw, 1 + (big ? 1 : 0), P.boot);
  }
  R(tx, ty, bw, th + P.coatLen, P.coat);                                                // the coat
  if (P.coatLen && !side) R(tx + (bw >> 1), ty + th, 1, P.coatLen, P.coatD);            // where it parts
  R(side ? (dir > 0 ? tx : tx + bw - 1) : tx + bw - 1, ty + 1, 1, th + P.coatLen - 1, P.coatD);
  R(tx, ty, bw, 1, P.coatL);                                                            // the lamplight on the shoulders
  if (P.scarf) { R(tx + (side ? 0 : 1), ty, bw - (side ? 0 : 2), Math.max(1, Math.round(P.h / 20)), P.scarf); if (view === 1 && big) R(tx + (bw >> 1) - 1, ty + 1, 1, Math.round(th * 0.45), P.scarf); }
  const armL = Math.max(1, Math.round(P.h / 24)), al = th - 1;
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1 + (walk && sg * (o.step ? 1 : -1) > 0 ? 1 : 0), armL, al, P.coatD); R(ax, ty + al + 1, armL, 1, P.skin); }
    else if (st === 1) { R(ax, hy - 1, armL, ty - hy + 2, P.coatD); R(ax, hy - 2, armL, 1, P.skin); }
    else if (st === 2) { R(ax, hy + hh, armL, ty - hy - hh + 2, P.coatD); R(ax - sg, hy + hh - 1, 1, 1, P.skin); }
    else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, armL, P.coatD); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); }
    else if (st === 4) { for (let k = 0; k < 4; k++) R(ax + sg * k, ty + 1 - k, armL, 1, P.coatD); R(ax + sg * 4, ty - 3, 1, 1, P.skin); }
    else if (st === 5) { R(sg < 0 ? ax - 3 : ax, ty + 2, 4, armL, P.coatD); }
    else { R(ax, ty + 1, armL, Math.round(th * 0.4), P.coatD); R(ax - sg * armL, ty + Math.round(th * 0.4), armL + 1, armL, P.coatD); }
  };
  if (side) {
    const st = o.aR || 0, ax = x + (walk ? (o.step ? -1 : 1) : 0) + bend * dir * 2;
    if (!st) { R(ax, ty + 1, armL, al, P.coatD); R(ax, ty + al + 1, armL, 1, P.skin); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, armL, P.coatD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else if (st === 1) { R(x + dir, hy - 1, armL, ty - hy + 2, P.coatD); R(x + dir, hy - 2, armL, 1, P.skin); }
    else if (st === 2) { for (let k = 0; k < 3; k++) R(x + dir * (1 + k), ty + 2 - k, 1, 1, P.coatD); R(x + dir * 4, ty - 1, 1, 1, P.skin); }
    else { R(x + dir, ty + 2, 2, armL, P.coatD); R(x + dir * 3, ty + 2, 1, 1, P.skin); }
  } else { arm(tx - armL, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1) + (o.lean || 0) + bend * dir * 2, hr = hh >= 5 ? 2 : 1, hY = hy + bend;
  if (view === 0) {
    R(hx, hY, hw, hh, P.hair);
    if (P.long) R(hx, hY + hh, hw, P.long, P.hair); else if (big) R(hx + 1, hY + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hY, hw, hh, P.skin); R(hx, hY, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hY + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hY + 1, 1, hh + P.long - 1, P.hair); }
    if (big && o.mouth) R(hx + (hw >> 1), hY + hh - 2, 1, 1, '#3a1a1a');
  } else { R(hx, hY, hw, hh, P.skin); R(hx, hY, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hY, 1, hh - 1 + P.long, P.hair); }
  let top = hY;
  if (P.top === 'tophat') { const th2 = Math.max(3, Math.round(P.h * 0.16)); R(hx - 1, hY, hw + 2, 1, P.hat); R(hx, hY - th2, hw, th2, P.hat); top = hY - th2; R(hx, top, hw, 1, '#d8d8e8'); }
  else if (P.top === 'bonnet') { R(hx - 1, hY - 1, hw + 2, hr + 1, P.hat); R(hx - 1, hY - 1, 1, hh, P.hat); R(hx + hw, hY - 1, 1, hh, P.hat); top = hY - 1; R(hx, top, hw, 1, '#d8d8e8'); }
  else if (P.hat) {                                                                      // a wool hat, a bobble, snow on it
    R(hx, hY - 1, hw, hr + 1, P.hat); R(hx, hY - 1, hw, 1, P.hatL);
    top = hY - 1;
    if (P.bobble) { const bb = big ? 2 + (hw & 1) : 1; R(hx + ((hw - bb + 1) >> 1), hY - 2 - (big ? 1 : 0), bb, big ? 2 : 1, P.bobble); top = hY - 2 - (big ? 1 : 0); }
    else R(hx + 1, top, Math.max(1, hw - 2), 1, '#d4d4e4');
  } else R(hx + 1, hY, Math.max(1, hw - 2), 1, mixHex(P.hair, '#e0e0f0', 0.5));       // snowflakes caught in the hair
  return top;
}
/** a breath cloud from a mouth at (x, y), every 4 s (seeded), drifting the way they face; s: 1 or 2 px */
function breath(x, y, t, seed, dir = 0, s = 1) {
  const u = frac(t / 4 + hash(seed, 3));
  if (u > 0.45) return;
  const k = u / 0.45;
  ctx.globalAlpha = 0.42 * (1 - k); ctx.fillStyle = '#e8e8f4';
  ctx.fillRect(Math.round(x + dir * (1 + k * 3)), Math.round(y - k * 4), s + (k > 0.4 ? 1 : 0), s);
  ctx.globalAlpha = 1;
}
/** where a person stands: [x, feet row, height px] for world X, Z and a height in metres */
const at = (X, Z, m = 1.72) => [sx(X, Z), sy(gY(Z), Z), F * m / Z];

/* the dog in its red coat, from behind (walking away), from the front or in profile (sniffing); s: px a metre */
function drawDog(x, fy, s, view, t, sniff) {
  x = Math.round(x); fy = Math.round(fy);
  const hgt = Math.max(4, Math.round(0.42 * s)), wag = step(t, 960) & 1;
  ctx.fillStyle = 'rgba(10,10,26,0.3)'; ctx.fillRect(x - 3, fy + 1, 7, 1);
  if (view === 2) {                                                                     // in profile, nose down in the snow
    const len = Math.max(6, Math.round(0.7 * s));
    R(x - (len >> 1), fy - hgt + 2, len, Math.max(2, hgt - 4), '#c0302a');             // the coat
    R(x - (len >> 1), fy - 2, 1, 2, '#3a2a1e'); R(x + (len >> 1) - 1, fy - 2, 1, 2, '#3a2a1e');
    R(x + (len >> 1), fy - hgt + (sniff ? 4 : 2), 2, 2, '#5a3e26'); if (sniff) R(x + (len >> 1) + 1, fy - 1, 1, 1, '#2a1a10');
    R(x - (len >> 1) - 1, fy - hgt + 1 - wag, 1, 2, '#5a3e26');
    return;
  }
  const w = Math.max(3, Math.round(0.26 * s));
  R(x - (w >> 1), fy - hgt + 2, w, Math.max(2, hgt - 4), '#c0302a');
  R(x - (w >> 1), fy - 1, 1, 2, '#3a2a1e'); R(x + (w >> 1), fy - 1, 1, 2, '#3a2a1e');
  R(x - 1, fy - hgt, 2, 2, '#5a3e26'); dot(x - 1, fy - hgt - 1, '#4a3220'); dot(x + 1, fy - hgt - 1, '#4a3220');
  if (view === 0) dot(x + (wag ? 1 : -1), fy - hgt + 2, '#5a3e26');                   // the tail, wagging
}
/* a bicycle walked beside its rider: seen end on (view 0 from behind, 1 from the front) or in profile */
function drawBike(x, fy, s, view, dir, t) {
  x = Math.round(x); fy = Math.round(fy);
  const wh = Math.max(4, Math.round(0.66 * s));
  if (view === 2) {
    const L = Math.max(8, Math.round(1.7 * s)), r = wh >> 1, fx = x + dir * (L >> 1), bx = x - dir * (L >> 1);
    for (const cx of [fx, bx]) { ctx.fillStyle = '#121218'; ctx.fillRect(cx - r, fy - wh, wh, 1); ctx.fillRect(cx - r, fy - 1, wh, 1); ctx.fillRect(cx - r, fy - wh, 1, wh); ctx.fillRect(cx + r - 1, fy - wh, 1, wh); }
    ctx.fillStyle = '#2a4a6a'; for (let k = 0; k <= L; k += 1) ctx.fillRect(bx + dir * k, Math.round(fy - r - k * 0.25), 1, 1);
    R(fx, fy - wh - 2, 1, 3, '#121218');
    dot(fx + dir, fy - wh - 1, '#fff4d0'); halo(HL.tiny, fx + dir * 2, fy - wh - 1, 0.8); dot(bx - dir, fy - wh + 1, '#ff3a30');
    return;
  }
  R(x, fy - wh, 1, wh, '#121218');                                                      // the wheel end on
  const hb = Math.max(3, Math.round(0.56 * s));
  R(x - (hb >> 1), fy - wh - Math.max(1, Math.round(0.3 * s)), hb, 1, '#1a1a22');
  if (view === 1) { dot(x, fy - wh, '#fff6dc'); halo(HL.tiny, x, fy - wh, 0.9); }
  else dot(x, fy - wh + 1, '#ff3a30');
}
/* the carollers' lantern on its pole, swaying a little */
function drawCarolLantern(x, top, t, sway) {
  const lx = Math.round(x + sway), ly = Math.round(top);
  R(lx, ly, 1, 6, '#2a2018');
  R(lx - 1, ly - 3, 3, 3, '#ffe4a8'); dot(lx, ly - 4, '#2a2018');
  halo(HL.carol, lx, ly - 2, 0.9 + 0.08 * wave(t, 120));
}
/* a car crawling along West Cedar in profile: boxy, snow on its roof, tyres in the slush, its lamps lit */
function drawCar(x, fy, s, dir, col) {
  x = Math.round(x); fy = Math.round(fy);
  const L = Math.round(4.3 * s), hb = Math.round(0.75 * s), hc = Math.round(0.55 * s), wr = Math.max(2, Math.round(0.32 * s));
  const x0 = x - (L >> 1);
  ctx.fillStyle = 'rgba(8,8,20,0.45)'; ctx.fillRect(x0 + 1, fy, L - 2, 1);
  R(x0, fy - wr - hb + 1, L, hb, col);
  R(x0, fy - wr - hb + 1, L, 1, mixHex(col, '#ffffff', 0.25));
  const c0 = x0 + Math.round(L * (dir > 0 ? 0.22 : 0.18)), cw = Math.round(L * 0.6);
  R(c0, fy - wr - hb - hc + 1, cw, hc, col);
  R(c0 + 1, fy - wr - hb - hc + 2, cw - 2, hc - 1, '#1a2030');
  R(c0 + Math.round(cw * 0.45), fy - wr - hb - hc + 2, 1, hc - 1, col);
  R(c0, fy - wr - hb - hc, cw, 1, '#e8e8f4');                                           // snow on the roof
  for (const wx of [x0 + Math.round(L * 0.2), x0 + Math.round(L * 0.78)]) { R(wx - wr, fy - wr * 2 + 1, wr * 2, wr * 2 - 1, '#0e0e12'); dot(wx, fy - wr, '#5a5a66'); }
  const fx = dir > 0 ? x0 + L - 1 : x0, bx = dir > 0 ? x0 : x0 + L - 1;
  dot(fx, fy - wr - hb + 2, '#fff8e0'); halo(HL.car, fx + dir * 2, fy - wr - hb + 2, 0.9);
  dot(bx, fy - wr - hb + 2, '#ff3a30'); halo(HL.tiny, bx, fy - wr - hb + 2, 0.6);
}

/* ================= The cast ================= */
const SHOV = def(1, 0, '#34465a', '#26262e', { hat: '#c8323a', bobble: '#ece8e0', scarf: '#d8c8a0', coatLen: 0.25 });
const PHOTOG = def(2, 1, '#4a563a', '#22222a', { hat: '#2a2a34', coatLen: 0.3 });
const NEIGHB = def(0, 3, '#7a4a7a', '#2a2a3a', { long: true, scarf: '#d84a3a', coatLen: 0.45, dk: 0.26 });
const HE = def(1, 1, '#2a3a5a', '#22222a', { hat: '#5a4a3a', scarf: '#9a2a2a', coatLen: 0.45 });
const SHE = def(0, 2, '#b89a72', '#1a1a22', { long: true, hat: '#ece8e0', bobble: '#ece8e0', coatLen: 0.55 });
const RIDER = def(3, 0, '#1e6a6a', '#22222a', { hat: '#1a1a1e', coatLen: 0.1 });
const FR_A = def(0, 4, '#c85a3a', '#2a2a3a', { long: true, hat: '#e8c040', bobble: '#ece8e0', coatLen: 0.3 });
const FR_B = def(2, 0, '#3a3a5e', '#22222a', { long: true, scarf: '#5ab0a0', coatLen: 0.35 });
const TR_A = def(1, 2, '#5a3a2a', '#26262e', { hat: '#2a4a6a', bobble: '#ece8e0' }), TR_B = def(3, 0, '#2a2a30', '#3a3a44', { hat: '#8a2a2a' });
const DAD = def(1, 1, '#3a4a3a', '#22222a', { hat: '#c8b898', scarf: '#2a3a6a', coatLen: 0.3 }), GIRL = def(0, 3, '#e86a9a', '#6a3a6a', { hat: '#ece8e0', bobble: '#ece8e0', dk: 0.2 });
const MUGW = def(0, 5, '#a8304a', '#2a2a34', { long: true, dk: 0.2 }), LISTENER = def(2, 5, '#4a3a2e', '#22222a', { dk: 0.2 });
const UMBW = def(1, 0, '#2a2a34', '#1a1a22', { coatLen: 0.4 }), DOGW2 = def(0, 2, '#3a5a7a', '#22222a', { hat: '#c8323a' });
const CAROLLERS = [
  def(0, 1, '#2a1616', '#1a1a1e', { hat: '#141418', top: 'tophat', scarf: '#c8282a', coatLen: 0.6 }),
  def(0, 3, '#5a2a3a', '#2a1a2a', { hat: '#4a2a5a', top: 'bonnet', long: true, coatLen: 0.9 }),
  def(2, 0, '#1e2a3a', '#1a1a1e', { hat: '#141418', top: 'tophat', scarf: '#2a6a3a', coatLen: 0.6 }),
  def(1, 4, '#2a4a3a', '#1a2a22', { hat: '#3a2a1a', top: 'bonnet', long: true, coatLen: 0.9 }),
  def(3, 0, '#4a3a2a', '#22222a', { hat: '#2a2a2a', scarf: '#d8c8a0', coatLen: 0.5 }),
];
/** where a walker is at t along keys [t, X, Z, lift m]; null outside them */
function walkPath(keys, t) {
  if (t < keys[0][0] || t >= keys[keys.length - 1][0]) return null;
  for (let i = 1; i < keys.length; i++) if (t < keys[i][0]) {
    const a = keys[i - 1], b = keys[i], u = (t - a[0]) / (b[0] - a[0]);
    const dX = b[1] - a[1], dZ = b[2] - a[2], moving = Math.abs(dX) + Math.abs(dZ) > 0.01;
    const view = !moving ? -1 : Math.abs(dX) > Math.abs(dZ) * 0.35 ? 2 : dZ > 0 ? 0 : 1;
    return { X: lerp(a[1], b[1], u), Z: lerp(a[2], b[2], u), lift: lerp(a[3] || 0, b[3] || 0, u), moving, view, dir: dX >= 0 ? 1 : -1, seg: i };
  }
  return null;
}
const stride = t => step(t, 768) & 1;
/** a walker on its feet at a path state; o: person options to add; returns [x, top, P] */
function walker(d, st, t, o = {}, m = 1.72, seed = 0) {
  const [x, fy0, hp] = at(st.X, st.Z, m), P = look(d, hp), fy = fy0 - st.lift * pxm(st.Z);
  const view = o.view ?? (st.view < 0 ? 1 : st.view);
  const top = person(x, fy, P, { view, dir: st.dir, step: st.moving ? stride(t + seed) : -1, ...o });
  if (!o.noBreath && o.alpha === undefined) breath(x + (view === 2 ? st.dir * ((P.hw >> 1) + 1) : 0), fy - P.ll - P.th - 2, t, d.id, view === 2 ? st.dir : 0, P.h > 24 ? 2 : 1);
  return [x, top, P, fy];
}
const faded = (a, fn) => { ctx.globalAlpha = a; fn(); ctx.globalAlpha = 1; };

/* --- the shoveller at his steps: push the snow to the curb, lift, toss, drag the shovel back --- */
const SHOV_AT = { X: -1.84, Z: 34.3 };
function actShoveller(t, put) {
  const { X, Z } = SHOV_AT, [x, fy, hp] = at(X, Z, 1.8), P = look(SHOV, hp), s = pxm(Z);
  const listen = inWin(t, BELLS.t0 + 1.5, BELLS.t0 + 13) || inWin(t, CAROLS.stop + 1, CAROLS.go);
  const wav = inWin(t, COUPLE.out + 1, COUPLE.out + 4), gust = inWin(t, FLURRY.p0 - 2, FLURRY.p1 + 2);
  put(Z, () => {
    const mouth = fy - P.ll - P.th - 2;
    const upright = (view, o = {}) => {
      person(x, fy, P, { view, aR: 6, ...o });
      const hx = x + (P.tw >> 1) + 1, hl = Math.round(P.h * 0.6);
      R(hx, fy - hl, 1, hl - 1, '#6a5038'); R(hx - 1, fy - 2, 3, 2, '#2a2c34'); dot(hx, fy - hl - 1, '#1a1a1e');
      breath(x, mouth, t, 11, 0, 2);
    };
    if (gust) return upright(1, { hunch: true });                                      // his back to the wind
    if (listen) return upright(0);
    if (wav) return upright(0, { aL: frac(t * 1.5) < 0.5 ? 1 : 4 });                   // goodnight to the couple
    const c = frac((t - 0.5) / 6), dir = 1, ty = tyOf(P, fy);
    let bx, by, bend = false, aR = 3;
    if (c < 0.42) { bx = x + dir * Math.round(lerp(0.3, 0.62, c / 0.42) * s); by = fy; bend = true; }
    else if (c < 0.56) { bx = x + dir * Math.round(0.6 * s); by = fy - Math.round(lerp(0, 0.3, (c - 0.42) / 0.14) * s); aR = 2; }
    else if (c < 0.7) { bx = x + dir * Math.round(0.66 * s); by = fy - Math.round(0.32 * s); aR = 2; }
    else { bx = x + dir * Math.round(lerp(0.62, 0.3, (c - 0.7) / 0.3) * s); by = fy; }
    person(x, fy, P, { view: 2, dir, bend, aR });
    const hx = x + dir * (bend ? 4 : 2), hy = ty + Math.round(P.th * 0.45) + (bend ? 2 : 0), n = Math.max(Math.abs(bx - hx), Math.abs(by - hy));
    for (let k = 0; k <= n; k++) dot(lerp(hx, bx, k / n), lerp(hy, by - 1, k / n), '#6a5038');
    R(bx - 1, by - 2, 4, 2, '#2a2c34'); R(bx - 1, by - 3, 4, 1, '#3e4250');
    if (c < 0.56) { const load = Math.round(clamp(c / 0.42, 0, 1) * 3); if (load) R(bx - 1, by - 3 - (load > 2 ? 1 : 0), 4, 1 + (load > 2 ? 1 : 0), '#e4e0ec'); }
    if (c >= 0.56 && c < 0.78) {                                                        // the snow flies to the curb
      const u = (c - 0.56) / 0.22;
      for (let k = 0; k < 4; k++) {
        const v = clamp(u - k * 0.06, 0, 1), px0 = bx + 2 + v * 0.9 * s + k, py0 = by - 4 - Math.sin(Math.PI * v) * 0.5 * s + v * (fy - by + 2);
        ctx.globalAlpha = 0.9 * (1 - v * 0.6); dot(px0, py0, '#eeeaf4'); ctx.globalAlpha = 1;
      }
    }
    breath(x + dir * 3, mouth + (bend ? 2 : 0), t, 11, dir, 2);
  });
}
/* --- the photographer at the tripod: at the viewfinder, now and then checking the screen --- */
const PH_AT = { X: 1.72, Z: 29.4 }, TRIPOD = { X: 1.24, Z: 30.4 };
function actPhotographer(t, put) {
  const [tx, tfy] = at(TRIPOD.X, TRIPOD.Z), cy = Math.round(sy(gY(TRIPOD.Z) + 1.46, TRIPOD.Z)), txr = Math.round(tx);
  const gust = inWin(t, FLURRY.p0 - 2, FLURRY.p1 + 2), carols = inWin(t, CAROLS.stop + 1, CAROLS.go), up = inWin(t, BELLS.t0 + 1, BELLS.t0 + 7);
  const c = frac((t + 3) / 20), check = !gust && !carols && !up && c > 0.62 && c < 0.85;
  put(TRIPOD.Z, () => {
    for (const [fx, fyy] of [[txr - 3, tfy], [txr + 3, tfy], [txr + 1, tfy + 1]]) {
      const n = Math.max(Math.abs(fx - txr), Math.abs(fyy - cy - 2));
      for (let k = 0; k <= n; k++) dot(lerp(txr, fx, k / n), lerp(cy + 2, fyy, k / n), '#1a1a20');
    }
    R(txr - 2, cy - 1, 5, 3, '#16161c'); R(txr - 1, cy - 2, 3, 1, '#2a2a32');
    R(txr - 1, cy, 3, 1, check ? '#9ad8ff' : '#2a3038');
    if (check) halo(HL.screen, txr, cy, 0.9);
    if (carols && frac(t / 4) < 0.08) dot(txr + 2, cy - 1, '#ff3a30');
  });
  put(PH_AT.Z, () => {
    const [x, fy, hp] = at(PH_AT.X, PH_AT.Z, 1.76), P = look(PHOTOG, hp);
    let o;
    // seen from behind, so his head stays square on his shoulders in every pose (a sideways lean read as a misplaced head)
    if (gust) o = { view: 0, aL: 5, aR: 0, hunch: true };                               // shielding the camera from the flurry
    else if (up) o = { view: 0 };                                                       // the bells: he looks up
    else if (check) o = { view: 0, aL: 3, aR: 0, hunch: true };
    else if (c > 0.85) o = { view: 0, aR: 2 };
    else o = { view: 0, aL: 3, aR: 0 };
    person(x, fy, P, o);
    R(x + (P.tw >> 1) - 1, tyOf(P, fy) + Math.round(P.th * 0.6), 3, 3, '#2a2420');       // his camera bag
    breath(x - 2, fy - P.ll - P.th - 2, t, 23, -1, 2);
  });
}
/* --- the wreath on the lamp post: the gust takes it (134), the neighbour hangs it back (162-190) --- */
const WREATH_GROUND = { X: -1.15, Z: 25.4 };
function drawWreath(X, Z, h, flat, shake) {
  const s = pxm(Z), cx = Math.round(sx(X, Z) + shake), cy = Math.round(sy(gY(Z) + h, Z));
  const rx = Math.max(2, Math.round(0.24 * s)), ry = flat ? Math.max(1, Math.round(0.07 * s)) : rx;
  for (let a = 0; a < 40; a++) {
    const th = a / 40 * TAU, x = Math.round(cx + Math.cos(th) * rx), y = Math.round(cy + Math.sin(th) * ry);
    dot(x, y, a % 5 === 0 ? '#2a5a32' : '#163a20');
    if (!flat && rx > 3) dot(Math.round(cx + Math.cos(th) * (rx - 1)), Math.round(cy + Math.sin(th) * (ry - 1)), a % 7 === 0 ? '#c0282a' : '#1e4628');
  }
  if (flat) { for (let k = -rx; k <= rx; k += 2) dot(cx + k, cy - ry, '#dcdae6'); return; }
  dot(cx - 1, cy + ry, '#d02a2a'); dot(cx + 1, cy + ry, '#d02a2a'); dot(cx, cy + ry + 1, '#a01e22');
  dot(cx - 1, cy - ry, '#e0dcea'); dot(cx, cy - ry, '#e0dcea');
}
function actWreath(t, put, present) {
  const N0 = NEIGH.door;
  const P = WREATH_POST, onPost = () => put(P.Z - 0.06, () => drawWreath(P.X, P.Z - 0.06, WREATH_H, false, 0));
  if (!present || t < FLURRY.gust || t >= N0 + 15) return onPost();
  if (t < FLURRY.gust + 1.6) {                                                         // tugged off, it drops into the snow
    const u = (t - FLURRY.gust) / 1.6;
    return put(lerp(P.Z, WREATH_GROUND.Z, u), () => drawWreath(lerp(P.X, WREATH_GROUND.X, u), lerp(P.Z - 0.06, WREATH_GROUND.Z, u), lerp(WREATH_H, 0.06, u * u), u > 0.85, 0));
  }
  if (t < N0 + 7.5) return put(WREATH_GROUND.Z, () => drawWreath(WREATH_GROUND.X, WREATH_GROUND.Z, 0.06, true, 0));
  if (t >= N0 + 12.5) return onPost();
  // in her hands: picked up, shaken off, carried to the post (drawn with her)
}
function actNeighbour(t, put) {
  const N0 = NEIGH.door;
  const H3 = HOUSES[3], yo = baseOf(H3) + 0.55 - gY(27.45);
  const keys = [[NEIGH.door, -2.36, 27.45, yo], [N0 + 2.5, -2.2, 27.45, yo], [N0 + 5, -1.6, 26.9, 0], [N0 + 7, -1.1, 25.4, 0], [N0 + 12.5, -1.0, 25.35, 0],
    [N0 + 14.5, -0.95, 25.6, 0], [N0 + 16, -0.95, 25.6, 0], [N0 + 17.5, -0.85, 24.4, 0], [N0 + 20.5, -0.85, 24.4, 0], [N0 + 22.5, -1.6, 26.9, 0], [N0 + 24.5, -2.22, 27.45, yo], [NEIGH.t1 - 1.5, -2.36, 27.45, yo]];
  const st = walkPath(keys, t);
  if (!st) return;
  put(st.Z, () => {
    const a = clamp(Math.min((t - NEIGH.door) / 1, (NEIGH.t1 - 1.5 - t) / 1), 0, 1);
    faded(a, () => {
      let o = {};
      if (inWin(t, N0 + 7, N0 + 9)) o = { view: 0, hunch: true, aL: 3, aR: 3 };                   // picking it out of the snow
      else if (inWin(t, N0 + 9, N0 + 12.5)) o = { view: 1, aL: 6, aR: 6 };                         // shaking it off
      else if (inWin(t, N0 + 12.5, N0 + 16)) o = { view: 2, dir: -1, aR: 3 };                       // hanging it back on the post
      else if (inWin(t, N0 + 17.5, N0 + 20.5)) o = { view: 0, aR: frac(t / 1.5) < 0.3 ? 2 : 0 };   // standing back to look
      const [x, top, P, fy] = walker(NEIGHB, st, t, { ...o, noBreath: a < 1 }, 1.66, 3);
      if (inWin(t, N0 + 7.5, N0 + 12.5)) {
        const held = inWin(t, N0 + 9, N0 + 12.5), s = pxm(st.Z), sh = held && frac(t * 3) < 0.5 ? 1 : 0;
        const wy = held ? sy(gY(st.Z) + 1.15, st.Z) : sy(gY(st.Z) + lerp(0.1, 1.0, (t - N0 - 7.5) / 1.5), st.Z);
        drawWreath(st.X, st.Z - 0.1, (sy(gY(st.Z), st.Z) - wy) / s, false, sh);
        if (held) for (let k = 0; k < 3; k++) { const { u } = cycle(t, 480, k / 3); ctx.globalAlpha = 0.7 * (1 - u); dot(x - 4 + k * 4, wy + 3 + u * 10, '#eceaf4'); ctx.globalAlpha = 1; }
      }
    });
  });
}
/* --- the couple and their dog: out of their door, goodnight to the shoveller, down the lane, right on West Cedar --- */
function actCouple(t, put) {
  const H4 = HOUSES[4], yo = baseOf(H4) + 0.55 - gY(33.7), { sniff } = COUPLE;
  const keys = [[COUPLE.door + 1.5, -2.3, 33.7, yo], [COUPLE.out + 1, -2.15, 33.75, yo], [COUPLE.out + 2.5, -1.3, 33.6, 0], [COUPLE.out + 4, -0.78, 35.4, 0],
    [sniff[0], -0.68, 42.4, 0], [sniff[1], -0.68, 42.4, 0], [COUPLE.end + 0.5, -0.42, 70.3, 0], [COUPLE.t1 - 1.5, 4.2, 70.6, 0]];
  const st = walkPath(keys, t);
  if (!st) return;
  const a = clamp((t - COUPLE.door - 1.5) / 1, 0, 1), sn = inWin(t, sniff[0], sniff[1]);
  const her = st, him = { ...st, X: st.X + (st.view === 2 ? 0 : 0.44), Z: st.Z + (st.view === 2 ? -0.35 : 0.08) };
  const dog = { X: st.X + (st.view === 2 ? 1.2 : 0.95), Z: st.Z + (st.view === 2 ? -0.1 : sn ? 1.5 : 1.2) };
  put(dog.Z, () => faded(a, () => { const [dx, dfy] = at(dog.X, dog.Z); drawDog(dx, dfy, pxm(dog.Z), sn ? 2 : st.view === 2 ? 2 : st.view === 1 ? 1 : 0, t, sn); }));
  put(him.Z, () => faded(a, () => {
    const [hx] = walker(HE, him, t, { aL: st.view === 0 ? 5 : 0, aR: 3, noBreath: a < 1, lean: sn ? -1 : 0 }, 1.8, 0.3);
    const [dx, dfy] = at(dog.X, dog.Z), hy = sy(gY(him.Z) + 0.95, him.Z), dy = dfy - Math.round(0.35 * pxm(dog.Z));   // the lead
    const n = Math.max(1, Math.abs(dx - hx - 3));
    ctx.fillStyle = '#c8323a'; for (let k = 1; k < n; k++) ctx.fillRect(Math.round(hx + 3 + (dx - hx - 3) * k / n), Math.round(lerp(hy, dy, k / n) + Math.sin(Math.PI * k / n) * 1.5), 1, 1);
  }));
  put(her.Z, () => faded(a, () => walker(SHE, her, t, { aR: st.view === 0 ? 5 : 0, aL: inWin(t, COUPLE.out + 1.5, COUPLE.out + 3) ? 1 : 0, noBreath: a < 1, lean: sn ? 1 : 0, view: inWin(t, COUPLE.out + 1, COUPLE.out + 3) ? 1 : undefined }, 1.66, 0)));
}
/* --- two friends come up from West Cedar; one slips on the cobbles, they laugh, they let themselves in --- */
function actFriends(t, put) {
  const H7 = HOUSES[7], yo = baseOf(H7) + 0.55 - gY(48.0), { slip, laugh } = FRIENDS;
  const keys = [[FRIENDS.t0, -6.6, 70.9, 0], [FRIENDS.up, -0.42, 70.7, 0], [FRIENDS.up + 1, -0.36, 69.8, 0], [slip, -0.4, 61.3, 0], [laugh + 3, -0.4, 60.9, 0],
    [FRIENDS.door, -1.1, 48.9, 0], [FRIENDS.door + 1.6, -2.2, 48.0, yo], [FRIENDS.t1, -2.4, 48.0, yo]];
  const st = walkPath(keys, t);
  if (!st) return;
  const a = clamp((FRIENDS.t1 - t) / 1.2, 0, 1), slipping = inWin(t, slip, slip + 2.4), laughing = inWin(t, slip + 2.4, laugh + 3);
  const b = { ...st, X: st.X + (st.view === 2 ? 0 : 0.5), Z: st.Z + (st.view === 2 ? 0.4 : 0.25) };
  const wob = slipping ? Math.round(Math.sin((t - slip) * 9) * 1.5) : 0;
  put(b.Z, () => faded(a, () => walker(FR_B, b, t, { aL: slipping ? 3 : laughing ? 6 : 0, mouth: laughing, noBreath: a < 1, view: slipping || laughing ? 1 : undefined }, 1.7, 0.4)));
  put(st.Z, () => faded(a, () => {
    const s2 = slipping ? { ...st, X: st.X + wob * 0.03 } : st;
    walker(FR_A, s2, t, { aL: slipping ? 1 : 0, aR: slipping ? 4 : laughing ? 2 : 0, lean: slipping ? wob : laughing && frac(t * 2) < 0.5 ? 1 : 0, mouth: laughing, noBreath: a < 1,
      view: slipping || laughing ? 1 : undefined, step: slipping ? (frac(t * 2) < 0.5 ? 0 : 1) : undefined }, 1.64, 0);
  }));
}
/* --- the delivery cyclist walks his bike up the cobbles, hands a bag in, walks it back down --- */
function actDelivery(t, put) {
  const { up, door, back, end } = DELIV, Zd = 46.3;
  const keys = [[DELIV.t0, -7, 70.8, 0], [up, -0.6, 70.6, 0], [up + 1, -0.42, 69.6, 0], [door, -0.4, Zd, 0], [door + 1, -1.5, Zd - 0.2, 0], [back - 1, -1.5, Zd - 0.2, 0],
    [back, -0.4, Zd, 0], [end, -0.4, 69.8, 0], [end + 1, -0.6, 70.7, 0], [DELIV.t1, -7.2, 70.9, 0]];
  const st = walkPath(keys, t);
  if (!st) return;
  const atDoor = inWin(t, door, back), bike = atDoor ? { X: -0.4, Z: Zd, view: 1, dir: 1 } : { X: st.X + (st.view === 2 ? 0 : 0.45), Z: st.Z, view: st.view < 0 ? 1 : st.view, dir: st.dir };
  put(bike.Z - 0.05, () => drawBike(sx(bike.X, bike.Z), sy(gY(bike.Z), bike.Z), pxm(bike.Z), bike.view, bike.dir, t));
  put(st.Z, () => {
    const handing = inWin(t, door + 1, back - 1);
    const [x, , P, fy] = walker(RIDER, st, t, handing ? { view: 2, dir: -1, aR: t < door + 3.5 ? 3 : 0 } : { aR: st.view === 2 ? 3 : 0 }, 1.78, 0.2);
    if (st.view === 0 && !handing) { const ty = tyOf(P, fy), bw = Math.max(3, Math.round(P.tw * 0.9)); R(x - (bw >> 1), ty - 1, bw, Math.round(P.th * 0.8), '#1e8a8a'); R(x - (bw >> 1), ty - 1, bw, 1, '#5ac8c8'); }
    if (handing && t < door + 3.5) R(x - 5, tyOf(P, fy) + 2, 2, 2, '#e8e0d0');            // the bag
  });
}
/* --- the Christmas tree, carried up from West Cedar and in at a door --- */
function actTree(t, put) {
  const H8 = HOUSES[8], yo = baseOf(H8) + 0.55 - gY(59.1);
  const kA = [[TREE.t0, 4.8, 70.6, 0], [TREE.t0 + 3, 0.2, 70.4, 0], [TREE.t0 + 4, 0.1, 69.5, 0], [TREE.door, -0.9, 60.0, 0], [TREE.door + 1.4, -2.1, 59.1, yo], [TREE.t1, -2.3, 59.1, yo]];
  const kB = kA.map(([tt, X, Z, l]) => [tt + 1.6, X + 0.1, Z + 0.4, l]);
  const A = walkPath(kA, t), B = walkPath(kB, t);
  const fa = clamp((TREE.t1 - t) / 1, 0, 1), fb = clamp((TREE.t1 + 1.6 - t) / 1, 0, 1);
  if (B) put(B.Z, () => faded(fb, () => walker(TR_B, B, t, { aL: 1, aR: 1, noBreath: fb < 1 }, 1.8, 0.7)));
  if (A && B) put((A.Z + B.Z) / 2, () => faded(Math.min(fa, fb), () => {                 // the tree between their shoulders, snow on it
    const [ax, afy] = at(A.X, A.Z), [bx, bfy] = at(B.X, B.Z), ay = sy(gY(A.Z) + 1.5 + A.lift, A.Z), by = sy(gY(B.Z) + 1.5 + B.lift, B.Z);
    const ra = 0.42 * pxm(A.Z), rb = 0.16 * pxm(B.Z);
    fillPolyCtx([[ax - ra, ay], [bx - rb, by - 1], [bx + rb, by - 1], [ax + ra, ay], [ax, ay + ra * 0.6]], (x, y) => ((x * 7 + y * 3) % 5 === 0 ? '#dcdcea' : (x + y) % 3 ? '#1a3a22' : '#24502c'));
  }));
  if (A) put(A.Z, () => faded(fa, () => walker(TR_A, A, t, { aL: 1, aR: 1, noBreath: fa < 1 }, 1.76, 0)));
}
/** fill a convex screen polygon straight onto the frame */
function fillPolyCtx(pts, col) {
  let y0 = Infinity, y1 = -Infinity;
  for (const [, y] of pts) { y0 = Math.min(y0, y); y1 = Math.max(y1, y); }
  for (let y = Math.ceil(y0); y <= Math.floor(y1); y++) {
    let xa = Infinity, xb = -Infinity;
    for (let i = 0; i < pts.length; i++) {
      const [ax, ay] = pts[i], [bx, by] = pts[(i + 1) % pts.length];
      if ((ay <= y && by > y) || (by <= y && ay > y)) { const x = ax + (y - ay) * (bx - ax) / (by - ay); xa = Math.min(xa, x); xb = Math.max(xb, x); }
    }
    for (let x = Math.round(xa); x <= Math.round(xb); x++) dot(x, y, col(x, y));
  }
}
/* --- a father pulls his little girl up the lane on a sled, and they go in --- */
function actSled(t, put) {
  const H6 = HOUSES[6], yo = baseOf(H6) + 0.55 - gY(46.1);
  const keys = [[SLED.t0, -6.5, 70.9, 0], [SLED.t0 + 4, -0.5, 70.6, 0], [SLED.t0 + 5, -0.4, 69.7, 0], [SLED.door, -0.5, 46.9, 0], [SLED.door + 1.6, -2.1, 46.1, yo], [SLED.t1, -2.3, 46.1, yo]];
  const st = walkPath(keys, t);
  if (!st) return;
  const a = clamp((SLED.t1 - t) / 1, 0, 1), onSled = t < SLED.door;
  const sled = onSled ? { X: st.X + (st.view === 2 ? -st.dir * 1.3 : 0.1), Z: st.Z + (st.view === 2 ? 0 : 1.3) } : { X: -0.6, Z: 47.3 };
  put(sled.Z, () => faded(a, () => {
    const [x, fy] = at(sled.X, sled.Z), s = pxm(sled.Z), w = Math.max(3, Math.round(0.42 * s));
    R(x - (w >> 1), fy - 1, w, 1, '#8a2a1e'); R(x - (w >> 1), fy - 2, w, 1, '#c8483a');
    if (onSled) { const g = look(GIRL, F * 0.62 / sled.Z); R(x - (g.tw >> 1), fy - 2 - g.th, g.tw, g.th, g.coat); R(x - (g.hw >> 1), fy - 2 - g.th - g.hh, g.hw, g.hh, g.skin); R(x - (g.hw >> 1), fy - 3 - g.th - g.hh, g.hw, 2, g.hat); }
  }));
  if (!onSled) put(47.0, () => faded(a, () => walker(GIRL, { X: -1.2, Z: 47.0, lift: 0, moving: false, view: 1, dir: 1 }, t, { noBreath: true }, 1.0)));
  put(st.Z, () => faded(a, () => walker(DAD, st, t, { aR: onSled ? 3 : 0, noBreath: a < 1 }, 1.8, 0.5)));
}
/* --- people in open doorways: a woman with a mug watching the snow, a neighbour listening to the carols --- */
function actDoorway(t, put, d, hi, t0, t1, mug) {
  if (!inWin(t, t0 + 0.8, t1 - 0.8)) return;
  const Ho = HOUSES[hi], Z = Ho.door + 0.5, yo = baseOf(Ho) + 0.55 - gY(Z);
  put(Z, () => {
    const [x, , P, fy] = walker(d, { X: XL + 0.2, Z, lift: yo, moving: false, view: 2, dir: 1 }, t, { view: 2, dir: 1, aR: mug ? 2 : 0 }, 1.68);
    if (mug) { const ty = tyOf(P, fy); R(x + 3, ty + 1, 2, 2, '#e8e0d0'); const { u } = cycle(t, 120, 0); ctx.globalAlpha = 0.5 * (1 - u); dot(x + 4, ty - 1 - u * 4, '#f0ecf4'); ctx.globalAlpha = 1; }
  });
}
/* --- along West Cedar, across the foot of the lane: the carollers, passers-by, the cars --- */
const CAROL_X = [-1.9, -0.95, 0, 0.95, 1.9];
function actCarols(t, put) {
  const { t0, stop, go, t1 } = CAROLS;
  if (t < t0 || t >= t1) return;
  CAROLLERS.forEach((d, i) => {
    const Z = 70.2 + (i % 2) * 0.5, X1 = CAROL_X[i];
    const keys = [[t0 + i * 0.8, X1 - 9.5, Z, 0], [stop + i * 0.4, X1, Z, 0], [go + (4 - i) * 0.5, X1, Z, 0], [t1, X1 + 8.5, Z, 0]];
    const st = walkPath(keys, t);
    if (!st) return;
    const singing = inWin(t, stop + 1.5, go), sway = singing ? Math.round(Math.sin(TAU * (t - stop) / 3 + i)) : 0;
    put(Z, () => {
      const [x, top, P, fy] = walker(d, singing ? { ...st, X: st.X + sway * 0.04 } : st, t, singing ? { view: 1, aL: 6, aR: 6, mouth: frac(t / 1.5 + i * 0.2) < 0.6 } : {}, 1.7, i);
      if (singing) R(x - 1, tyOf(P, fy) + 2, 3, 2, '#f0ece0');                              // the song book
      if (singing) breath(x, fy - P.ll - P.th - 1, t * 2, 50 + i, 0, 1);
      if (i === 4) drawCarolLantern(x + 3, top - 4, t, singing ? Math.sin(TAU * t / 6) : 0);
    });
  });
}
function actPassers(t, put, crowd) {
  WC_WALK.forEach((w, i) => {
    if (!here(i ? RANK.wc2 : RANK.wc1, crowd) || !inWin(t, w.t0, w.t1)) return;
    const u = (t - w.t0) / (w.t1 - w.t0), X = w.dir > 0 ? lerp(-6, 6, u) : lerp(6, -6, u), Z = 78.6;
    const st = { X, Z, lift: 0, moving: true, view: 2, dir: w.dir };
    put(Z, () => {
      const [x, top] = walker(i ? DOGW2 : UMBW, st, t, { aR: w.umbrella ? 3 : 0 }, 1.74, i);
      if (w.umbrella) { R(x - 3, top - 3, 7, 1, '#1a1a24'); R(x - 2, top - 4, 5, 1, '#1a1a24'); R(x - 2, top - 5, 5, 1, '#e0dcea'); R(x, top - 2, 1, 2, '#1a1a24'); }
      if (w.dog) drawDog(sx(X + w.dir * 0.9, Z), sy(gY(Z), Z), pxm(Z), 2, t, false);
    });
  });
}
function actCars(t, put) {
  for (const c of CARS) {
    if (!inWin(t, c.t0, c.t0 + 6)) continue;
    const u = (t - c.t0) / 6, X = c.dir > 0 ? lerp(-9, 9, u) : lerp(9, -9, u), Z = 74.2;
    put(Z, () => drawCar(sx(X, Z), sy(gY(Z), Z), pxm(Z), c.dir, c.col));
    const hx = sx(X + c.dir * 3.2, ZH - 0.2), hy = syG(0.7, ZH);                          // the headlights wash over the end house
    put(ZH - 0.5, () => halo(HL.car, hx, hy, 0.5));
  }
}

/* ================= The lane's small life: doors, the cat, the flag, smoke, pigeons, a squirrel ================= */
// doors that open (house, from, to, whose): the light spills over the steps and the snow
const DOOR_OPEN = [[7, 23.6, 27.6, 'friends'], [8, 40.2, 44.4, 'tree'], [4, 56.2, 60.4, 'couple'], [7, 63.5, 90.5, 'doorstep'],
  [6, 117.4, 122.8, 'deliv'], [3, NEIGH.door, NEIGH.t1 - 1.5, 'neigh'], [6, 185.8, 190.4, 'sled'], [9, LISTEN.t0, LISTEN.t1, 'carols']];
const CAT_WIN = [1, 0, 1];                           // the cat's window: house, floor, bay
const FLAG = { hi: 6, Z: 44.2 };
let DOOR_LIT, CAT_SPR, SMOKE, PIGEON_PERCH;
const CAT_ROWS = [
  ['....k.k.', '....kkkk', '....kekk', '....kkk.', '...kkk..', '..kkkk..', '.kkkkk..', '.kkkkk..', 'kkkkkkt.', 'kkkkkkt.', '.kkkk.t.'],
  ['....k.k.', '....kkkk', '....kekk', '....kkk.', '...kkk..', '..kkkk..', '.kkkkk..', '.kkkkk..', 'kkkkkk..', 'kkkkkkt.', '.kkkkt..'],
  ['...k.k..', '...kkkk.', '...ekek.', '...kkkk.', '...kkk..', '..kkkk..', '.kkkkk..', '.kkkkk..', 'kkkkkkt.', 'kkkkkkt.', '.kkkk.t.'],
];
function prerenderCast() {
  LOOKS.clear();
  DOOR_LIT = HOUSES.map(Ho => {
    const p = pix(W, H), b = baseOf(Ho), d0 = Ho.door + 0.04, d1 = Ho.door + DOOR_W - 0.04;
    fillPoly(p, proj([[XL + 0.02, b + 2.75, d0], [XL + 0.02, b + 2.75, d1], [XL + 0.02, b + 0.56, d1], [XL + 0.02, b + 0.56, d0]]), (x, y) => pick(['#e8a858', '#ffd088', '#fff0c8'], 0.4 + 0.5 * bayer(x, y), x, y));
    return { lit: trim(p), x: sx(XL + 0.9, Ho.door + 0.55), y: sy(gY(Ho.door + 0.55) + 0.05, Ho.door + 0.55), s: pxm(Ho.door) };
  });
  CAT_SPR = CAT_ROWS.map(rows => sprite(rows, { k: '#141016', e: '#e8c050', t: '#141016' }));
  SMOKE = [[ehx(-1.1), ehy(13.5) - 1, 1], [ehx(2.1), ehy(13.5) - 1, 2], [CHIM9.x, CHIM9.y - 1, 3], [412, 64, 4], [388, 60, 5]].map(([x, y, sd]) => ({ x, y, sd }));
  const top = Math.round(cfy(CORNER.top));
  PIGEON_PERCH = [0, 1, 2, 3, 4].map(i => ({ x: BB.x + 3 + i * 9 + (i > 2 ? 3 : 0), y: top - 4 }));
}
function drawDoors(t, crowd) {
  for (const [hi, t0, t1, who] of DOOR_OPEN) {
    if (!inWin(t, t0, t1) || !here(RANK[who], crowd)) continue;
    const a = env(t, t0, t1, 0.7), D = DOOR_LIT[hi];
    blit(D.lit, a);
    ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = a * 0.8;                 // the light laid over the steps and the snow
    const w = Math.round(HL.door.r * 2 * clamp(D.s / 12, 0.6, 2.2)), h = Math.round(w * 0.42);
    ctx.drawImage(HL.door.c, Math.round(D.x - w / 2), Math.round(D.y - h / 2), w, h);
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  }
}
/* the cat on the sill of its lit window, back-lit: its tail flicks, now and then it turns to look at us */
function drawCat(t) {
  const [hi, f, b] = CAT_WIN, Ho = HOUSES[hi], c = WINDOWS(Ho).ground[b], x = Math.round(sx(XL, c - 0.18)), y = Math.round(sy(baseOf(Ho) + FLOORS[f][0] + 0.02, c - 0.18));
  const look = frac(t / 30 + 0.3) < 0.12, flick = frac(t / 7.5) < 0.14;
  ctx.drawImage(CAT_SPR[look ? 2 : flick ? 1 : 0], x - 4, y - 11);
}
/* the Stars and Stripes on its bracket half-way down the lane, stirring (and tugged hard in the flurry) */
function drawFlag(t) {
  const Ho = HOUSES[FLAG.hi], b = baseOf(Ho), Z = FLAG.Z;
  const p0 = [sx(XL, Z), sy(b + 4.4, Z)], p1 = [sx(XL + 1.25, Z), sy(b + 5.2, Z)];
  const n = Math.round(Math.hypot(p1[0] - p0[0], p1[1] - p0[1]));
  for (let k = 0; k <= n; k++) dot(lerp(p0[0], p1[0], k / n), lerp(p0[1], p1[1], k / n), '#c8b878');
  const f = flurry(t), len = Math.round(0.9 * pxm(Z)), wid = Math.round(1.05 * pxm(Z) * (0.75 + 0.25 * (1 - f)));
  for (let i = 0; i < wid; i++) {                                                       // columns out from the pole
    const u = i / wid, px0 = lerp(p0[0], p1[0], 0.15 + u * 0.85), py0 = lerp(p0[1], p1[1], 0.15 + u * 0.85) + 1;
    const ripple = Math.sin(TAU * (u * 1.4 - (8 + 18 * f) * t / LOOP * 6)) * (0.6 + 1.6 * f) * u;
    for (let j = 0; j < len; j++) {
      const yy = Math.round(py0 + j + ripple * 0.6 + (1 - f) * u * 1.5), xx = Math.round(px0 + ripple * 0.3 + f * j * 0.15);
      const canton = u < 0.42 && j < len * 0.54;
      const col = canton ? ((i + j) % 3 === 0 && u > 0.05 ? '#d8d8e8' : '#1e2a5a') : (Math.floor(j / Math.max(1, len / 7)) % 2 ? '#d8d4dc' : '#a82a2e');
      dot(xx, yy, mixHex(col, NIGHT, 0.18));
    }
  }
}
/* a silhouette at the end house's windows: someone crossing a room, someone come to listen to the bells */
function drawEndFigures(t, crowd) {
  if (!here(RANK.windows, crowd)) return;
  const bell = env(t, BELLS.t0 + 5, BELLS.t1 - 4, 1.2);
  if (bell > 0.02) { const x = Math.round(ehx(0.3)) - 1, y = Math.round(ehy(5.7)) + 3; faded(bell, () => { R(x, y + 2, 3, 6, '#2a1a14'); R(x, y, 3, 2, '#3a2418'); }); }
  const u = frac(t / 60 + 0.2);
  if (u < 0.05) { const x = Math.round(ehx(-1.9) - 3 + u / 0.05 * 7), y = Math.round(ehy(2.9)) + 2; R(x, y + 2, 2, 7, '#2a1a14'); R(x, y, 2, 2, '#3a2418'); }
}
/* chimney smoke, drifting up the lane in the wind (harder in the flurry) */
function drawSmoke(t) {
  const f = flurry(t);
  for (const s of SMOKE) for (let i = 0; i < 7; i++) {
    const u = frac(t * (6 + s.sd) / LOOP * 4 + i / 7 + hash(s.sd, 1)), r = 1 + Math.round(u * 2.2);
    const x = s.x + u * (10 + 26 * f) * (s.sd % 2 ? 1 : -0.6) + Math.sin(TAU * (u * 2 + s.sd * 0.3)) * 1.5, y = s.y - u * (20 - 12 * f);
    ctx.globalAlpha = 0.26 * (1 - u) * (u < 0.1 ? u / 0.1 : 1); ctx.fillStyle = '#9a94aa';
    ctx.fillRect(Math.round(x - r / 2), Math.round(y - r / 2), r, r);
  }
  ctx.globalAlpha = 1;
}
/* pigeons hunched on the corner building's parapet; the first bell starts them up, they wheel over the lane and settle */
function drawPigeons(t) {
  const { t0 } = BELLS, fly0 = t0 + 0.3, land = t0 + 26, back = t0 + 31;
  PIGEON_PERCH.forEach((p, i) => {
    let x = p.x, y = p.y, flying = false;
    if (inWin(t, fly0 + i * 0.25, back + i * 0.4)) {
      const a = TAU * ((t - fly0) / 9 + i / 5), cx = 300 + 10 * Math.sin(TAU * (t - fly0) / 23), cy = 26;
      const ox = cx + Math.cos(a) * 34, oy = cy + Math.sin(a) * 10;
      const k = smooth(fly0 + i * 0.25, fly0 + i * 0.25 + 2.5, t) * (1 - smooth(land + i * 0.4, back + i * 0.4, t));
      x = lerp(p.x, ox, k); y = lerp(p.y, oy, k); flying = k > 0.05;
    }
    x = Math.round(x); y = Math.round(y);
    if (flying) { const up = step(t, 2400) % 2 === i % 2; R(x - 1, y, 3, 1, '#5a5a6a'); dot(x - 2, up ? y - 1 : y + 1, '#4a4a5a'); dot(x + 2, up ? y - 1 : y + 1, '#4a4a5a'); }
    else { R(x, y, 3, 2, '#4a4a58'); dot(x + (i % 2 ? 2 : 0), y - 1, '#6a6a7a'); dot(x + 1, y, '#5a5a6c'); }
  });
}
function drawTwigs(t) {
  const f = flurry(t);
  for (const m of TWIGS) if (wave(t, m.k, m.ph) > 0.6 - 0.5 * f) dot(m.x + treeSway(m.ti, t) + (wave(t, m.k, m.ph) > 0.9 ? 1 : 0), m.y - 1, '#2a2632');
}
/* the squirrel along the garden wall's coping */
function drawSquirrel(t) {
  for (const t0 of SQUIRREL) {
    if (!inWin(t, t0, t0 + 8)) continue;
    const u = t - t0, Z = u < 3 ? lerp(46, 34, u / 3) : u < 4.5 ? 34 : lerp(34, 21, (u - 4.5) / 3.5);
    const x = Math.round(sx(XR - 0.08, Z)), y = Math.round(sy(gY(Z) + GARDEN.top + 0.12 * Math.sin(Z * 0.7), Z)) - 1;
    const a = clamp(Math.min(u / 0.5, (8 - u) / 0.6), 0, 1), hop = u < 3 || u > 4.5 ? (step(t, 1920) & 1) : 0;
    faded(a, () => { R(x - 1, y - 2 - hop, 3, 2, '#6a6670'); dot(x + 2, y - 3 - hop, '#7a7680'); R(x - 3, y - 4 - hop, 2, 3, '#8a8690'); dot(x - 3, y - 5 - hop, '#9a96a0'); });
  }
}
/* the West Cedar post lamp in front of the end house */
const WC_LAMP = { X: -1.4, Z: 78.2 };

/* ================= Per-frame passes ================= */
function castItems(t, crowd) {
  const L = [], put = (Z, fn) => L.push([Z, fn]);
  if (crowd > 0) {
    actShoveller(t, put); actPhotographer(t, put); actNeighbour(t, put); actDelivery(t, put); actCarols(t, put);
    if (here(RANK.couple, crowd)) actCouple(t, put);
    if (here(RANK.friends, crowd)) actFriends(t, put);
    if (here(RANK.tree, crowd)) actTree(t, put);
    if (here(RANK.sled, crowd)) actSled(t, put);
    if (here(RANK.doorstep, crowd)) actDoorway(t, put, MUGW, 7, DOORSTEP.t0, DOORSTEP.t1, true);
    actDoorway(t, put, LISTENER, 9, LISTEN.t0, LISTEN.t1, false);
    actPassers(t, put, crowd);
  }
  actWreath(t, put, crowd > 0);
  actCars(t, put);
  return L;
}
let ITEMS = [];
function drawFarLife(t, crowd) {
  ITEMS = castItems(t, crowd);
  FAR_PEOPLE = here(RANK.windows, crowd);
  drawFarWindows(t, false);
  drawEndFigures(t, crowd);
  const far = ITEMS.filter(i => i[0] >= ZEND - 0.3).sort((a, b) => b[0] - a[0]);
  ctx.save(); ctx.beginPath(); ctx.rect(0, 0, Math.floor(sx(XR, ZEND)) + 1, H); ctx.clip();
  const lx = Math.round(sx(WC_LAMP.X, WC_LAMP.Z)), ly = Math.round(syG(3.0, WC_LAMP.Z));
  let lampDone = false;
  for (const [Z, fn] of far) { if (!lampDone && Z < WC_LAMP.Z) { drawWCLamp(lx, ly, t); lampDone = true; } fn(); }
  if (!lampDone) drawWCLamp(lx, ly, t);
  ctx.restore();
}
function drawWCLamp(x, y, t) {
  R(x, y + 2, 1, Math.round(syG(0, WC_LAMP.Z)) - y - 2, '#0c0c12');
  R(x - 1, y - 2, 3, 4, '#ffe6b0'); dot(x, y - 3, '#d8d8e8');
  halo(HL.window, x, y, 0.9);
}
function drawLane(t, crowd) {
  const L = ITEMS.filter(i => i[0] < ZEND - 0.3);
  POSTS.forEach((P, i) => L.push([P.Z, () => blit(POST_C[i])]));
  L.sort((a, b) => b[0] - a[0]);
  for (const [, fn] of L) fn();
}
function drawFrontLife(t, crowd) {
  drawIvy(t);
  drawSprigs(t);
  drawWindowLife(t, crowd);
  drawNearRight(t, crowd);
  drawDoors(t, crowd);
  drawCat(t);
  drawFlag(t);
  drawClumps(t);
  drawSquirrel(t);
}
function drawSkyLife(t) { drawSmoke(t); }
function drawGroundLife(t, dry) { if (dry) drawGlitter(t); }

/* ================= The settled snow's own life: drifting powder, glitter, clumps that let go ================= */
// What stays when the falling snow stops (a picked clear night, a picked autumn): the wind still lifts the dry powder
// off the cobbles and drives it up the lane in low wisps, the crust glitters under the lamps, and clumps slide off the
// sills, the cornices and the garden walls' coping. Wet picks (rain, drizzle, a storm) lay the powder, so no wisps.
const WISPS = [];
{
  const r = rng(4242);
  for (let i = 0; i < 46; i++) WISPS.push({ k: 5 + Math.floor(r() * 6), ph: r(), X: i % 3 === 0 ? lerp(XL + 0.15, XCL - 0.2, r()) : i % 3 === 1 ? lerp(XCL, XR - 0.2, r()) : lerp(XCR - 0.4, XR - 0.1, r()),
    w: 0.6 + r() * 1.3, len: 2.5 + r() * 4, n: 14 + Math.floor(r() * 10), seed: 900 + i, lift: i % 3 === 0 ? 0.7 : 0.35 });
}
for (const w of WISPS) w.s = Array.from({ length: w.n }, (_, j) => ({ dz: (hash(w.seed, j, 1) - 0.5) * w.len, dx: (hash(w.seed, j, 2) - 0.5) * w.w,
  h: hash(w.seed, j, 3) * w.lift, l: 0.25 + hash(w.seed, j, 4) * 0.5 }));
const WISP_FAR = ZEND + 2, WISP_NEAR = 7.5;
function drawWisps(t) {
  const f = flurry(t);
  ctx.fillStyle = '#eceaf6';
  for (const w of WISPS) {
    const u = frac(w.k * t / LOOP + w.ph), Zc = lerp(WISP_FAR, WISP_NEAR, u), a = 0.34 * Math.min(1, u / 0.08, (1 - u) / 0.12) * (1 + 0.6 * f);
    if (a <= 0.01) continue;
    ctx.globalAlpha = Math.min(0.62, a);
    const sway = Math.sin(TAU * (u * 2 + w.ph)) * 0.35;
    for (const st of w.s) {                                                           // streaks of powder, each riding the wisp
      const Z = Zc + st.dz;
      if (Z < WISP_NEAR || Z > WISP_FAR) continue;
      const X = clamp(w.X + st.dx + sway, XL + 0.05, XR - 0.05);
      const x = sx(X, Z), y = sy(gY(Z) + st.h, Z), len = Math.max(1, Math.round(st.l * F / Z));
      ctx.fillRect(Math.round(x - len / 2), Math.round(y), len, 1);
    }
  }
  ctx.globalAlpha = 1;
}
let GLITTER;
function makeGlitter() {
  const g = [], r = rng(515);
  for (let tries = 0; g.length < 1500 && tries < 30000; tries++) {
    const y = Math.floor(lerp(181, H - 1, Math.pow(r(), 0.8))), Z = zOfG(y + 0.5);
    if (Z > ZEND) continue;
    const X = lerp(XL + 0.05, XR - 0.05, r());
    if (CLEARED.some(c => X > c.X0 && X < c.X1 && Z > c.Z0 && Z < c.Z1)) continue;
    const L = lampAt(X, gY(Z), Z);
    g.push({ x: Math.round(sx(X, Z)), y, k: 2 + Math.floor(r() * 6), ph: r(), c: L > 0.5 ? '#fffaf0' : '#e8f0ff' });
  }
  return g;
}
function drawGlitter(t) {
  for (const s of GLITTER) { const v = frac(s.k * t / LOOP + s.ph); if (v < 0.035) dot(s.x, s.y, s.c); }
}
// clumps of snow let go: [where (X, Z, height over the ground or the house's floor, house or -1), two times]
const CLUMPS = [
  { X: XR - 0.05, Z: 7.4, h: 3.12, hi: -1, at: [12, 131] }, { X: XR - 0.05, Z: 9.6, h: 3.12, hi: -1, at: [57, 177] },
  { X: XR - 0.05, Z: 11.8, h: 3.12, hi: -1, at: [33, 152] }, { X: XR - 0.05, Z: 6.3, h: 3.12, hi: -1, at: [83, 203] },
  { X: XR - 0.05, Z: 8.4, h: 3.12, hi: -1, at: [105, 227] }, { X: XR - 0.05, Z: 15.5, h: 3.12, hi: -1, at: [70, 191] },
  { X: XL + 0.06, Z: 6.4, h: 2.92, hi: 0, at: [44, 164] }, { X: XL + 0.06, Z: 8.9, h: 3.58, hi: 0, at: [96, 216] },
  { X: XL + 0.06, Z: 11.2, h: 2.92, hi: 1, at: [20, 140] }, { X: XL + 0.06, Z: 13.0, h: 5.75, hi: 1, at: [66, 186] },
  { X: XL + 0.06, Z: 7.6, h: 2.92, hi: 0, at: [118, 236] }, { X: XL + 0.06, Z: 15.2, h: 3.58, hi: 1, at: [8, 128] },
  { X: XL + 0.1, Z: 33.6, h: 9.8, hi: 4, at: [22, 145] }, { X: XL + 0.1, Z: 44.6, h: 10.0, hi: 6, at: [92, 212] },
];
function drawClumps(t) {
  for (const c of CLUMPS) for (const t0 of c.at) {
    if (!inWin(t, t0, t0 + 2.2)) continue;
    const b = c.hi >= 0 ? baseOf(HOUSES[c.hi]) : gY(c.Z), top = b + c.h, g = gY(c.Z), fall = Math.sqrt(2 * (top - g) / 9.8);
    const s = pxm(c.Z), x = Math.round(sx(c.X, c.Z)), tt = t - t0, sz = Math.max(1, Math.round(0.07 * s));
    if (tt < fall) { const Y = top - 4.9 * tt * tt; R(x, Math.round(sy(Y, c.Z)), sz + 1, sz, '#eeedf6'); if (sz > 1) dot(x + 1, Math.round(sy(Y, c.Z)) - 1, '#dcdbe8'); }
    else { const v = (tt - fall) / (2.2 - fall); ctx.globalAlpha = 0.85 * (1 - v); const gy = Math.round(sy(g, c.Z)); for (let k = -2; k <= 2; k++) R(x + k * Math.round(1 + v * 2 * (1 + sz / 2)), gy - 1 - Math.round(Math.abs(k) * v * 2), sz, 1, '#eeedf6'); ctx.globalAlpha = 1; }
  }
}

/* ================= Life behind the windows ================= */
// Christmas trees in a few windows, their lights twinkling slowly; someone crossing a lit room now and then
const XMAS = [[0, 0, 1], [1, 0, 0], [1, 1, 2], [2, 1, 0], [3, 1, 1]];          // house, floor, bay
const CROSSERS = [[1, 0, 1, 6, 0.1], [2, 0, 0, 5, 0.45], [1, 1, 1, 4, 0.7], [3, 0, 1, 6, 0.3], [4, 1, 0, 5, 0.85], [0, 0, 1, 5, 0.6],
  [2, 1, 1, 4, 0.2], [3, 1, 0, 5, 0.55], [3, 2, 1, 3, 0.9], [4, 2, 2, 4, 0.4]];   // house, floor, bay, crossings a loop, phase
let XMAS_C, XMAS_LIGHTS, CROSS_BOX;
/** a window's glass, clipped to the frame: [x0, y0, x1, y1] (screen), and its depth */
function glassBox(hi, f, b) {
  const Ho = HOUSES[hi], c = (f ? WINDOWS(Ho).upper : WINDOWS(Ho).ground)[b], [s0, s1] = FLOORS[f], base = baseOf(Ho);
  return { x0: Math.max(0, Math.ceil(sx(XL, c - 0.44))), x1: Math.min(W - 1, Math.floor(sx(XL, c + 0.44))), y0: Math.ceil(sy(base + s1 - 0.04, c)), y1: Math.floor(sy(base + s0 + 0.12, c)), c, base };
}
function makeWindowLife() {
  XMAS_LIGHTS = [];
  const p = pix(W, H);
  XMAS.forEach(([hi, f, b], i) => {
    WINDOW_OVERRIDE.set(winKey(hi, f, b), true);                                       // a tree's window is lit
    const g = glassBox(hi, f, b), w = g.x1 - g.x0 + 1, hgt = Math.round((g.y1 - g.y0) * 0.8), cx = g.x0 + w * 0.55, r = rng(300 + i);
    for (let y = g.y1 - hgt; y <= g.y1; y++) {
      const u = (y - (g.y1 - hgt)) / hgt, half = Math.max(0.5, u * w * 0.48);
      for (let x = Math.round(cx - half); x <= Math.round(cx + half); x++) if (x >= g.x0 && x <= g.x1) p.set(x, y, (x + y * 2) % 5 === 0 ? '#24502e' : '#163a20');
    }
    const n = Math.max(4, Math.round(w * hgt / 30));
    for (let k = 0; k < n; k++) {
      const u = 0.12 + r() * 0.85, y = Math.round(g.y1 - hgt + u * hgt), half = u * w * 0.44, x = Math.round(cx + (r() * 2 - 1) * half);
      if (x >= g.x0 && x <= g.x1) XMAS_LIGHTS.push({ x, y, c: ['#ff5040', '#ffd040', '#60e070', '#60a0ff', '#ffffff'][Math.floor(r() * 5)], k: 3 + Math.floor(r() * 9), ph: r() });
    }
    p.set(Math.round(cx), g.y1 - hgt - 1, '#ffe080');                                  // the star
  });
  XMAS_C = trim(p);
  CROSS_BOX = CROSSERS.map(([hi, f, b, k, ph]) => { WINDOW_OVERRIDE.set(winKey(hi, f, b), true); return { ...glassBox(hi, f, b), k, ph }; });
}
function drawWindowLife(t, crowd) {
  blit(XMAS_C);
  for (const L of XMAS_LIGHTS) if (frac(L.k * t / LOOP + L.ph) < 0.62) dot(L.x, L.y, L.c);
  if (here(RANK.windows, crowd)) for (const B of CROSS_BOX) {                                                          // a figure crosses the room behind the glass
    const u = frac(B.k * t / LOOP + B.ph) / 0.05;
    if (u >= 1) continue;
    const w = B.x1 - B.x0 + 1, bw = Math.max(1, Math.round(w * 0.45)), x = Math.round(lerp(B.x0 - bw, B.x1 + 1, u)), top = Math.round(lerp(B.y0, B.y1, 0.12));
    ctx.save(); ctx.beginPath(); ctx.rect(B.x0, B.y0, w, B.y1 - B.y0 + 1); ctx.clip();
    R(x, top + Math.round((B.y1 - B.y0) * 0.16), bw, B.y1 - top, '#3a2416'); R(x + Math.round(bw * 0.2), top, Math.max(1, Math.round(bw * 0.6)), Math.round((B.y1 - B.y0) * 0.16), '#4a2e1c');
    ctx.restore();
  }
}

/* the ivy's leaves flipping pale in the wind, in gusts that run along the walls (bucketed: a table lookup per leaf) */
const IVY_Q = 128;
let IVY_X, IVY_Y, IVY_B1, IVY_B2, IVY_COLS, IVY_RUNS;
function packIvy() {
  const cols = [...new Set(IVY.map(L => L.c))], order = IVY.map((L, i) => i).sort((a, b) => cols.indexOf(IVY[a].c) - cols.indexOf(IVY[b].c));
  const n = IVY.length;
  IVY_X = new Int16Array(n); IVY_Y = new Int16Array(n); IVY_B1 = new Uint8Array(n); IVY_B2 = new Uint8Array(n); IVY_COLS = cols; IVY_RUNS = [];
  let last = -1;
  order.forEach((i, k) => {
    const L = IVY[i], ci = cols.indexOf(L.c);
    IVY_X[k] = L.x; IVY_Y[k] = L.y; IVY_B1[k] = Math.floor(frac(L.ph) * IVY_Q); IVY_B2[k] = Math.floor(frac(L.ph * 1.7) * IVY_Q);
    if (ci !== last) { IVY_RUNS.push([k, ci]); last = ci; }
  });
  IVY_RUNS.push([n, -1]);
}
const IVY_A = new Float32Array(IVY_Q), IVY_B = new Float32Array(IVY_Q);
function drawIvy(t) {
  const f = flurry(t), th = 0.84 - 0.3 * f, p1 = 31 * t / LOOP, p2 = 83 * t / LOOP;
  for (let b = 0; b < IVY_Q; b++) { IVY_A[b] = 0.7 * Math.sin(TAU * (p1 - b / IVY_Q)); IVY_B[b] = 0.3 * Math.sin(TAU * (p2 + b / IVY_Q)); }
  for (let r = 0; r < IVY_RUNS.length - 1; r++) {
    ctx.fillStyle = IVY_COLS[IVY_RUNS[r][1]];
    for (let k = IVY_RUNS[r][0], e = IVY_RUNS[r + 1][0]; k < e; k++) if (IVY_A[IVY_B1[k]] + IVY_B[IVY_B2[k]] > th) ctx.fillRect(IVY_X[k], IVY_Y[k], 1, 1);
  }
}
/* the window of the corner house at Willow Street, on the right edge: its tree's lights, someone at the glass */
const NR_WIN = { x0: 456, x1: 471 };
function drawNearRight(t, crowd) {
  const y0 = Math.round(syG(2.9, 5.0)), y1 = Math.round(syG(1.3, 5.0));
  for (let k = 0; k < 26; k++) {
    const x = NR_WIN.x0 + Math.floor(hash(k, 1, 77) * (NR_WIN.x1 - NR_WIN.x0)), y = Math.round(lerp(y0 + 20, y1 - 4, hash(k, 2, 77)));
    if (frac((3 + (k % 7)) * t / LOOP + hash(k, 3, 77)) < 0.6) dot(x, y, ['#ff5040', '#ffd040', '#60e070', '#60a0ff'][k % 4]);
  }
  const u = frac(5 * t / LOOP + 0.37) / 0.06;
  if (u < 1 && here(RANK.windows, crowd)) { const x = Math.round(lerp(NR_WIN.x0 - 8, NR_WIN.x1 + 2, u)); ctx.save(); ctx.beginPath(); ctx.rect(NR_WIN.x0 - 4, y0, NR_WIN.x1 - NR_WIN.x0 + 8, y1 - y0); ctx.clip(); R(x, y0 + 30, 9, y1 - y0 - 30, '#3a2416'); R(x + 2, y0 + 18, 5, 12, '#4a2e1c'); ctx.restore(); }
}

/* the end house's and the corner building's windows: rooms lit and left, someone passing the glass */
const END_WINS = [];
let FAR_PEOPLE = true;                              // someone may pass the far windows (not at crowd 0)
function makeFarWindows() {
  END_WINS.length = 0;
  FLOORS.slice(0, 3).forEach(([s0, s1], f) => (f ? [-1.9, 0.3, 2.5] : [-1.9, 2.5]).forEach((X, b) => {
    const x0 = Math.round(ehx(X) - 3), y0 = Math.round(ehy(s1)), h = Math.round(ehy(s0)) - y0, r = hash(f, b, 61);
    END_WINS.push({ x0, y0, w: 7, h, k: 2 + Math.floor(r * 4), ph: hash(f, b, 62), duty: 0.72, cross: (f + b) % 2 === 0, kc: 4 + ((f + b) % 3) });
  }));
  for (const [wx, wy] of [[cfx(CORNER.X0) + 8, cfy(7.6)], [cfx(CORNER.X0) + 30, cfy(7.6)], [cfx(CORNER.X0) + 8, cfy(4.6)]])
    END_WINS.push({ x0: Math.round(wx), y0: Math.round(wy), w: 6, h: 9, k: 3, ph: hash(Math.round(wx), 63), duty: 0.7, cross: true, kc: 5, corner: true });
}
function drawFarWindows(t, corner) {
  for (const w of END_WINS) {
    if (!!w.corner !== corner) continue;
    if (frac(w.k * t / LOOP + w.ph) > w.duty) { R(w.x0, w.y0, w.w, w.h, '#1c1622'); R(w.x0, w.y0 + w.h - 1, w.w, 1, '#2a2230'); continue; }
    const u = frac(w.kc * t / LOOP + w.ph * 3) / 0.07;
    if (w.cross && u < 1 && FAR_PEOPLE) { const x = Math.round(lerp(w.x0 - 3, w.x0 + w.w, u)); for (let y = w.y0 + 2; y < w.y0 + w.h; y++) for (let k = 0; k < 3; k++) if (x + k >= w.x0 && x + k < w.x0 + w.w) dot(x + k, y, y < w.y0 + 4 ? '#4a2e1c' : '#3a2416'); }
  }
}

/* ================= Prerendered state ================= */
let SKY_C, CLOUDS, DECK, END_C, WC_C, CORNER_C, CORNER_BARE, AD_C, PITCH, BACKS_C, TREE_C, FRONT_C, WALL_C, GROUND_C, POST_C, HL, LAMP_HL,
  VEIL_C, VIG_C, TOGGLES;
function renderVeil() {                              // the snowy air: thickest towards the end of the lane
  const p = pix(W, H);
  for (let y = 0; y < 215; y++) for (let x = 120; x < 420; x++) {
    const d = Math.hypot((x - 264) / 105, (y - 138) / 80), v = Math.max(0, 1 - d) ** 1.1;
    const q = Math.floor(v * 6 + bayer(x, y)) / 6;
    if (q > 0) p.set(x, y, [156, 150, 178], Math.round(q * 0.85 * 255));
  }
  return p.done();
}
function renderVignette() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.7, 1.4, Math.hypot((i - W / 2) / (W / 2), (y - H * 0.5) / (H / 2) * 0.9)), q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, [6, 6, 16], Math.round(q * 0.42 * 255));
  }
  return p.done();
}
function prerender() {
  TWIGS.length = 0; IVY.length = 0; SPRIGS.length = 0; WINDOW_OVERRIDE.clear();
  WINDOW_OVERRIDE.set(winKey(...CAT_WIN), true);                    // the cat's window is always lit
  makeWindowLife();                                                 // and so are those with a tree or someone in them
  GLITTER = makeGlitter();
  makeFarWindows();
  const r = rng(2026);
  HL = {
    tiny: makeHalo(3, '#fff0c8', 0.5, 1.1), sign: makeHalo(20, '#ffe0b0', 0.14, 1.5), car: makeHalo(12, '#fff4dc', 0.35, 1.3),
    window: makeHalo(10, '#ffc070', 0.16, 1.4), carol: makeHalo(10, '#ffcc80', 0.4, 1.3), screen: makeHalo(3, '#9ad0ff', 0.45, 1.2),
    door: makeHalo(16, '#ffc070', 0.2, 1.5),
  };
  SKY_C = renderSky();
  CLOUDS = [makeCloud(11, 70, '#2a2640', '#3e3248', 150, 90), makeCloud(23, 60, '#34304a', '#4a3a50', 130, 70), makeCloud(37, 50, '#3c3650', '#56425a', 110, 54)];
  BACKS_C = renderBacks(r);
  TREE_C = TREES.map((T, i) => renderTree(T, i));
  DECK = makeDeck(53, 128);
  CORNER_C = renderCorner(true); const nIvy = IVY.length; CORNER_BARE = renderCorner(false); IVY.length = nIvy;   // its leaves once
  AD_C = renderAd();
  PITCH = makePitch(BB.w, BB.h, { accent: ACCENT });
  END_C = renderEndHouse(r); WC_C = renderWestCedar();
  const fp = renderFront();
  paintProps(fp);
  FRONT_C = trim(fp);
  TOGGLES = TOGGLE_DEF.map(([hi, f, b, k, ph, duty]) => {             // each toggling window painted in its other state
    const box = winBox(hi, f, b), on = winLit(hi, f, b);
    WINDOW_OVERRIDE.set(winKey(hi, f, b), !on);
    const q = renderFront(box);
    WINDOW_OVERRIDE.delete(winKey(hi, f, b));
    const [c, x] = makeCanvas(box[2] - box[0], box[3] - box[1]);
    x.drawImage(q.done(), -box[0], -box[1]);
    return { c, x: box[0], y: box[1], k, ph, duty, on };
  });
  WALL_C = renderWall();
  packIvy();
  GROUND_C = renderGround();
  POST_C = POSTS.map(renderPost);
  LAMP_HL = LIGHTS.map((L, i) => {
    const s = pxm(L.Z), post = i < POSTS.length;
    return { x: sx(L.X, L.Z), y: sy(L.Y + (post ? 0.05 : 0), L.Z), big: makeHalo(Math.round(clamp(s * (post ? 1.9 : 1.4), 6, 46)), '#ffbe70', post ? 0.17 : 0.13, 1.7),
      core: makeHalo(Math.round(clamp(s * 0.42, 2, 14)), '#fff2d0', 0.42, 1.3), Z: L.Z, ph: hash(i, 3) };
  });
  FLAKE_LIGHTS = LIGHTS.map((L, i) => { const s = pxm(L.Z); const rx = clamp(s * (i < POSTS.length ? 1.5 : 1.0), 3, 40); return [sx(L.X, L.Z), sy(L.Y, L.Z), rx, rx * 1.3, '#ffe8c0']; });
  WX = { land: SNOW_L.map(L => L.land), lights: FLAKE_LIGHTS.map(([x, y, rx, ry, col]) => ({ x, y, rx, ry, col })) };
  VEIL_C = renderVeil(); VIG_C = renderVignette();
  prerenderCast();
}

/* ================= Per-frame ================= */
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  const d = Math.floor(frac(t / LOOP + 0.5 / W) * W);                                   // the cloud deck drifts across, one width a loop
  ctx.drawImage(DECK, -d, 0); ctx.drawImage(DECK, W - d, 0);
  CLOUDS.forEach((c, i) => {                                                          // each layer steps a pixel at its own moments
    const off = Math.floor(frac((i + 1) * t / LOOP + 0.5 / W) * W), y = [-6, 30, 70][i];
    ctx.drawImage(c, -off, y); ctx.drawImage(c, W - off, y);
  });
  const f = flurry(t);
  if (f > 0.01) { ctx.globalAlpha = 0.3 * f; ctx.fillStyle = '#6a6280'; ctx.fillRect(0, 0, W, 182); ctx.globalAlpha = 1; }
}
/* a picked thunderstorm's distant lightning: a soft swell in the cloud over the roofs (none with reduced motion) */
function drawStormSky(t, weather, reducedMotion) {
  if (weather !== 'thunderstorm') return;
  const a = stormLight(t, reducedMotion);
  if (a > 0) { ctx.globalAlpha = a * 1.4; ctx.fillStyle = '#a6add2'; ctx.fillRect(0, 0, W, 182); ctx.globalAlpha = 1; }
}
// Beacon Hill's gas mantles burn steady: the halos only swell while the flurry fills the air with flakes to light
function drawLampHalos(t, from, to) {
  const f = flurry(t);
  for (const L of LAMP_HL) if (L.Z >= from && L.Z < to) {
    const I = 0.97 * (1 + 0.35 * f);
    halo(L.big, L.x, L.y, I); halo(L.core, L.x, L.y, I);
  }
}
function drawBoard(t, pitch) {
  halo(HL.sign, BB.x + 24, BB.y + 6, 0.75);
  if (!PITCH.covers(t, pitch)) ctx.drawImage(AD_C, BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  for (const lx of [BB.x + 13, BB.x + BB.w - 8]) { dot(lx, BB.y - 5, '#fff6dc'); halo(HL.tiny, lx, BB.y - 4, 0.9); }   // the bulbs under the hoods
}
function drawToggles(t) {
  for (const T of TOGGLES) if ((frac(T.k * t / LOOP + T.ph) < T.duty) !== T.on) ctx.drawImage(T.c, T.x, T.y);
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures) on the painted sign; billboard: false leaves the sign out (the loop
// videos); weather: a WEATHERS pick (site/js/engine/weather.js), the city's own (snow) when left out; crowd: the share
// of the people to show, 0..1 (CROWD when left out or not a number); 0 = nobody at all
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather);
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  drawSky(t);
  drawStormSky(t, weather, reducedMotion);
  drawSkyLife(t, crowd);
  blit(BACKS_C);
  blit(WC_C); blit(END_C);
  drawFarLife(t, crowd);
  blit(billboard ? CORNER_C : CORNER_BARE);
  drawFarWindows(t, true);
  if (billboard) drawBoard(t, pitch);
  drawPigeons(t);
  for (let i = TREE_C.length - 1; i >= 0; i--) { blit(TREE_C[i].a); ctx.drawImage(TREE_C[i].b.c, TREE_C[i].b.x + treeSway(i, t), TREE_C[i].b.y); }
  drawTwigs(t);
  blit(GROUND_C);
  const dry = !wx.shared || wx.shared === 'snow' || wx.shared === 'leaves';           // wet picks lay the powder
  drawGroundLife(t, dry);
  if (wx.snow) drawSnow(SNOW_L[0], t);
  drawWeather(ctx, wx.shared, 0, t, WX, reducedMotion);
  blit(WALL_C); blit(FRONT_C);
  drawToggles(t);
  drawFrontLife(t, crowd);
  if (dry) drawWisps(t);
  ctx.globalAlpha = 0.1 + 0.62 * flurry(t); ctx.drawImage(VEIL_C, 0, 0); ctx.globalAlpha = 1;
  drawLampHalos(t, 40, 999);
  if (wx.snow) drawSnow(SNOW_L[1], t);
  drawWeather(ctx, wx.shared, 1, t, WX, reducedMotion);
  drawLane(t, crowd);
  drawLampHalos(t, 0, 40);
  if (wx.snow && flurry(t) > 0.01) { ctx.globalAlpha = 0.14 * flurry(t); ctx.fillStyle = '#9a94b0'; ctx.fillRect(0, 0, W, H); ctx.globalAlpha = 1; }   // the flurry's white-out
  if (wx.snow) drawSnow(SNOW_L[2], t);
  drawWeather(ctx, wx.shared, 2, t, WX, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'boston-acorn-street',
  name: 'Boston',
  tagline: 'Snow on Acorn Street',
  timeZone: 'America/New_York',
  accent: '#44bb11',                                   // the city's (the Green Line's green), written out: tools/seo/seo.mjs reads it from the source
  focusX: 266,
  landmark: [228, 342],                                // the lane from its middle to its foot: the receding fronts, the end house, the corner flank
  landmarkTop: 40,                                     // the rooflines' chimneys over the far half of the lane
  billboard: BB,
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  spots: [
    { kind: 'cat', x: Math.round(sx(XL, WINDOWS(HOUSES[CAT_WIN[0]]).ground[CAT_WIN[2]] - 0.18)) - 5, y: Math.round(sy(baseOf(HOUSES[CAT_WIN[0]]) + FLOORS[0][0], WINDOWS(HOUSES[CAT_WIN[0]]).ground[CAT_WIN[2]])) - 12, w: 10, h: 12 },
    { kind: 'window', x: Math.round(ehx(-1.9) - 3), y: Math.round(ehy(2.9)), w: 7, h: Math.round(ehy(1.3)) - Math.round(ehy(2.9)) },
    { kind: 'window', x: Math.round(postTop(WREATH_POST).x) - 5, y: Math.round(postTop(WREATH_POST).y) - 7, w: 10, h: 12 },
  ],
  ambience: { wind: 0.22, traffic: 0.04 },
  events: [
    ...BELL_T.map((t, i) => ({ t, type: 'bell', x: 430, gain: i === 0 ? 0.34 : 0.24 })),
    ...[CAROLS.stop + 1.5, CAROLS.stop + 8, CAROLS.stop + 14.5, CAROLS.stop + 21].map(t => ({ t, type: 'chime', x: 262, gain: 0.16 })),
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
